// ============================================================================
// TRAINING ANALYSIS SERVICE - ENTERPRISE EDITION v3.0
// ============================================================================
// Complete training analysis with:
// - 80+ term expanded glossary for absolute beginners to pros
// - Full session analysis with detailed examples
// - All original exports preserved
// ============================================================================

import {
  TrainingSession,
  LoggedDecision,
  Position,
  PositionCategory,
  Street,
  ActionType,
  BoardTexture,
  HandStrength,
  DrawType,
  getPositionCategory,
} from './trainingDecisionLogger';

// ============================================================================
// POKER GLOSSARY - EXPANDED 80+ TERMS
// ============================================================================

export interface GlossaryEntry {
  term: string;
  definition: string;
  simpleDefinition?: string;
  example?: string;
  visualExample?: string;
  level: 'absolute-beginner' | 'beginner' | 'intermediate' | 'advanced';
  category: 'basics' | 'actions' | 'structure' | 'hand-rankings' | 'positions' | 'streets' | 'strategy' | 'stats' | 'player-types' | 'tournament';
  relatedTerms?: string[];
  commonMistake?: string;
  proTip?: string;
}

export const POKER_GLOSSARY: Record<string, GlossaryEntry> = {
  // ============================================================================
  // ABSOLUTE BASICS - What is Poker?
  // ============================================================================
  poker: {
    term: 'Poker',
    definition: 'A card game where players compete to win chips by having the best hand or making all other players fold.',
    simpleDefinition: 'A card game where you try to win chips by having the best cards OR by making everyone else give up!',
    example: 'You win by either showing the best 5-card hand at the end, or by betting so much that everyone else folds.',
    level: 'absolute-beginner',
    category: 'basics',
    relatedTerms: ['texas_holdem', 'hand', 'pot', 'fold'],
    proTip: 'Poker is about making good decisions, not just having good cards!',
  },
  texas_holdem: {
    term: 'Texas Hold\'em',
    definition: 'The most popular poker variant where each player gets 2 private cards and shares 5 community cards to make the best 5-card hand.',
    simpleDefinition: 'The main type of poker! You get 2 secret cards, then 5 cards go in the middle for everyone to share.',
    example: 'You have A K, the board shows Q J 10 2 3. You have a Royal Flush using A with the board!',

    level: 'absolute-beginner',
    category: 'basics',
    relatedTerms: ['hole_cards', 'community_cards', 'flop', 'turn', 'river'],
  },
  hand: {
    term: 'Hand',
    definition: 'Either the cards you hold, or one complete round of poker from dealing to showdown.',
    simpleDefinition: 'Your cards, OR one complete game from start to finish.',
    example: '"I had a great hand" (good cards) vs "We played 50 hands tonight" (50 rounds).',
    level: 'absolute-beginner',
    category: 'basics',
  },
  pot: {
    term: 'Pot',
    definition: 'The total chips in the middle that players are competing to win.',
    simpleDefinition: 'All the chips in the middle that the winner gets!',
    example: 'The pot is $100. If you win, you get all $100.',
    level: 'absolute-beginner',
    category: 'basics',
    relatedTerms: ['bet', 'raise', 'side_pot'],
  },
  chips: {
    term: 'Chips',
    definition: 'Tokens representing money used for betting in poker.',
    simpleDefinition: 'The colored discs you bet with instead of real money.',
    level: 'absolute-beginner',
    category: 'basics',
    relatedTerms: ['stack', 'buy_in'],
  },
  stack: {
    term: 'Stack',
    definition: 'The total chips a player has at the table.',
    simpleDefinition: 'All the chips in front of you.',
    example: 'You have a $500 stack. Your opponent has $300.',
    level: 'absolute-beginner',
    category: 'basics',
    relatedTerms: ['chips', 'all_in'],
  },
  showdown: {
    term: 'Showdown',
    definition: 'When remaining players reveal their cards after all betting is complete to determine the winner.',
    simpleDefinition: 'The end where everyone shows their cards to see who wins!',
    example: 'After the river betting, both players show their hands. Best hand wins the pot.',
    level: 'absolute-beginner',
    category: 'basics',
    relatedTerms: ['hand', 'pot'],
  },

  // ============================================================================
  // BETTING ACTIONS
  // ============================================================================
  fold: {
    term: 'Fold',
    definition: 'To give up your hand and exit the current round, forfeiting any chips already bet.',
    simpleDefinition: 'Give up and throw away your cards. You lose what you\'ve already bet but don\'t risk more.',
    example: 'You have 7-2, someone bets big. You fold because your hand is weak.',
    level: 'absolute-beginner',
    category: 'actions',
    relatedTerms: ['check', 'call', 'bet', 'raise'],
    proTip: 'Folding is NOT losing - it\'s saving chips for better spots!',
    commonMistake: 'Playing too many hands instead of folding weak ones.',
  },
  check: {
    term: 'Check',
    definition: 'To pass the action without betting when no bet has been made. Essentially a "free" pass to see the next card.',
    simpleDefinition: 'Pass your turn without betting. Only works if no one else bet first!',
    example: 'No one has bet. You check to see the next card for free.',
    level: 'absolute-beginner',
    category: 'actions',
    relatedTerms: ['bet', 'check_raise'],
    commonMistake: 'Checking strong hands instead of betting for value.',
  },
  call: {
    term: 'Call',
    definition: 'To match the current bet to stay in the hand.',
    simpleDefinition: 'Match what someone else bet to stay in the game.',
    example: 'Opponent bets $10. You call by putting in $10.',
    level: 'absolute-beginner',
    category: 'actions',
    relatedTerms: ['bet', 'raise', 'fold'],
    commonMistake: 'Calling too often instead of raising or folding.',
  },
  bet: {
    term: 'Bet',
    definition: 'To put chips into the pot when no one else has bet yet.',
    simpleDefinition: 'Be the first to put chips in on this betting round.',
    example: 'The pot is $20, no one has bet. You bet $15.',
    level: 'absolute-beginner',
    category: 'actions',
    relatedTerms: ['raise', 'check', 'value_bet', 'bluff'],
  },
  raise: {
    term: 'Raise',
    definition: 'To increase the current bet, forcing others to put in more chips to continue.',
    simpleDefinition: 'Someone bet, now you bet MORE to make them pay extra to stay in.',
    example: 'Opponent bets $10. You raise to $30. Now they must call $20 more or fold.',
    level: 'absolute-beginner',
    category: 'actions',
    relatedTerms: ['bet', 'three_bet', 'reraise'],
  },
  all_in: {
    term: 'All-In',
    definition: 'To bet all of your remaining chips. You can\'t be forced out of the hand even if others bet more.',
    simpleDefinition: 'Push ALL your chips in! You can\'t bet more but can still win.',
    example: 'You have $100. You go all-in. Even if opponent bets $500, you can win up to $200 (your $100 + their matching $100).',
    level: 'absolute-beginner',
    category: 'actions',
    relatedTerms: ['side_pot', 'stack'],
    proTip: 'Going all-in should be for strong hands or well-timed bluffs, not desperation!',
  },
  limp: {
    term: 'Limp',
    definition: 'To just call the big blind preflop instead of raising. Generally considered a weak play.',
    simpleDefinition: 'Just calling the minimum bet before the flop instead of raising.',
    example: 'Blinds are $1/$2. Instead of raising, you just call $2.',
    level: 'beginner',
    category: 'actions',
    commonMistake: 'Limping too much. Raising is usually better!',
    proTip: 'If your hand is worth playing, it\'s usually worth raising.',
  },
  three_bet: {
    term: '3-Bet',
    definition: 'The third bet in a sequence: 1st bet is the blind, 2nd is a raise, 3rd is a re-raise.',
    simpleDefinition: 'Re-raising someone who already raised.',
    example: 'Blinds posted (1st bet), Player A raises (2nd bet), you re-raise (3-bet).',
    level: 'intermediate',
    category: 'actions',
    relatedTerms: ['raise', 'four_bet'],
  },
  check_raise: {
    term: 'Check-Raise',
    definition: 'To check initially, then raise after an opponent bets. A powerful move showing strength or as a bluff.',
    simpleDefinition: 'Check first, then raise when someone bets. Tricky move!',
    example: 'You check, opponent bets $20, you raise to $60.',
    level: 'intermediate',
    category: 'actions',
    relatedTerms: ['check', 'raise', 'slow_play'],
    proTip: 'Check-raising is great with strong hands to build bigger pots.',
  },

  // ============================================================================
  // GAME STRUCTURE
  // ============================================================================
  blinds: {
    term: 'Blinds',
    definition: 'Forced bets posted by two players before cards are dealt. Creates initial pot to compete for.',
    simpleDefinition: 'Forced bets that 2 players must pay before seeing cards. This creates something to win!',
    example: 'In a $1/$2 game, small blind posts $1, big blind posts $2.',
    level: 'absolute-beginner',
    category: 'structure',
    relatedTerms: ['small_blind', 'big_blind', 'button'],
  },
  small_blind: {
    term: 'Small Blind (SB)',
    definition: 'The smaller of the two forced bets, posted by the player directly left of the dealer button.',
    simpleDefinition: 'The smaller forced bet, posted by player to the left of the dealer.',
    example: 'In $1/$2, the small blind is $1.',
    level: 'absolute-beginner',
    category: 'structure',
    relatedTerms: ['big_blind', 'blinds', 'button'],
  },
  big_blind: {
    term: 'Big Blind (BB)',
    definition: 'The larger forced bet, posted by the player two seats left of the dealer. This sets the minimum bet.',
    simpleDefinition: 'The bigger forced bet. This is the minimum you must call to play.',
    example: 'In $1/$2, the big blind is $2. You must call at least $2 to see the flop.',
    level: 'absolute-beginner',
    category: 'structure',
    relatedTerms: ['small_blind', 'blinds'],
  },
  button: {
    term: 'Button (BTN)',
    definition: 'The dealer position, marked by a round chip. Best position at the table because you act LAST after the flop.',
    simpleDefinition: 'The "dealer" seat - the BEST position because you see what everyone does before you decide!',
    example: 'On the Button, you see all 5 opponents act before making your decision.',
    level: 'absolute-beginner',
    category: 'structure',
    relatedTerms: ['position', 'blinds', 'cutoff'],
    proTip: 'Play more hands on the Button - you have maximum information advantage!',
  },
  ante: {
    term: 'Ante',
    definition: 'A small forced bet that ALL players must post before each hand. Creates bigger pots.',
    simpleDefinition: 'A small bet everyone pays every hand (in some games).',
    level: 'beginner',
    category: 'structure',
  },
  side_pot: {
    term: 'Side Pot',
    definition: 'A separate pot created when a player is all-in but others continue betting. The all-in player can only win the main pot.',
    simpleDefinition: 'Extra pot created when someone\'s all-in but others keep betting.',
    example: 'Player A goes all-in for $50. Players B and C have more chips and bet $100 more. That extra $100 goes in a side pot.',
    level: 'intermediate',
    category: 'structure',
    relatedTerms: ['all_in', 'pot'],
  },

  // ============================================================================
  // STREETS (Betting Rounds)
  // ============================================================================
  preflop: {
    term: 'Preflop',
    definition: 'The first betting round, after players receive their two hole cards but before any community cards are dealt.',
    simpleDefinition: 'First betting round - you only have your 2 private cards.',
    example: 'You look at your cards: A K. This is preflop. You decide to raise.',

    level: 'absolute-beginner',
    category: 'streets',
    relatedTerms: ['flop', 'hole_cards'],
  },
  flop: {
    term: 'Flop',
    definition: 'The first three community cards dealt face-up, followed by a betting round.',
    simpleDefinition: '3 cards dealt to the middle that everyone shares. Second betting round.',
    visualExample: '🃏 K Q 7',

    example: 'The flop comes K Q 7. Now you combine these with your hole cards.',

    level: 'absolute-beginner',
    category: 'streets',
    relatedTerms: ['preflop', 'turn', 'community_cards'],
  },
  turn: {
    term: 'Turn',
    definition: 'The fourth community card, dealt after flop betting is complete.',
    simpleDefinition: 'The 4th shared card. Third betting round.',
    visualExample: '🃏 K Q 7 [5]',

    example: 'After the flop, the turn card 5 is dealt. More betting happens.',

    level: 'absolute-beginner',
    category: 'streets',
    relatedTerms: ['flop', 'river'],
  },
  river: {
    term: 'River',
    definition: 'The fifth and final community card. Last chance to make your hand!',
    simpleDefinition: 'The 5th and LAST shared card. Final betting round before showdown.',
    visualExample: '🃏 K Q 7 5 [A]',

    example: 'The river A completes the board. This is your final hand.',

    level: 'absolute-beginner',
    category: 'streets',
    relatedTerms: ['turn', 'showdown'],
    proTip: 'River decisions are huge - the pot is biggest and there\'s no more cards to save you!',
  },
  hole_cards: {
    term: 'Hole Cards',
    definition: 'Your two private cards that only you can see.',
    simpleDefinition: 'Your 2 secret cards that only YOU can see!',
    visualExample: ' A K',

    example: 'You peek at your hole cards: A K - a premium hand!',

    level: 'absolute-beginner',
    category: 'streets',
    relatedTerms: ['community_cards', 'preflop'],
  },
  community_cards: {
    term: 'Community Cards',
    definition: 'The five shared cards in the middle that all players use to make their hand.',
    simpleDefinition: 'The 5 cards in the middle that EVERYONE shares.',
    visualExample: '🃏🃏🃏🃏🃏 K Q 7 5 A',

    level: 'absolute-beginner',
    category: 'streets',
    relatedTerms: ['flop', 'turn', 'river', 'hole_cards'],
  },

  // ============================================================================
  // HAND RANKINGS
  // ============================================================================
  royal_flush: {
    term: 'Royal Flush',
    definition: 'The best possible hand: A-K-Q-J-10 all of the same suit.',
    simpleDefinition: 'THE BEST HAND! A-K-Q-J-10 all same suit. Unbeatable!',
    visualExample: 'A K Q J 10',

    level: 'absolute-beginner',
    category: 'hand-rankings',
    relatedTerms: ['straight_flush'],
    proTip: 'You\'ll get dealt a Royal Flush about once every 650,000 hands!',
  },
  straight_flush: {
    term: 'Straight Flush',
    definition: 'Five consecutive cards of the same suit.',
    simpleDefinition: '5 cards in a row, all same suit.',
    visualExample: '7 8 9 10 J',

    level: 'absolute-beginner',
    category: 'hand-rankings',
    relatedTerms: ['straight', 'flush', 'royal_flush'],
  },
  four_of_a_kind: {
    term: 'Four of a Kind (Quads)',
    definition: 'Four cards of the same rank.',
    simpleDefinition: '4 cards of the same number/face.',
    visualExample: 'K K K K 5',

    level: 'absolute-beginner',
    category: 'hand-rankings',
    relatedTerms: ['full_house', 'three_of_a_kind'],
  },
  full_house: {
    term: 'Full House (Boat)',
    definition: 'Three of a kind plus a pair.',
    simpleDefinition: '3 of one kind + 2 of another.',
    visualExample: 'Q Q Q 7 7',

    example: 'Queens full of sevens - you have three Queens and two 7s.',
    level: 'absolute-beginner',
    category: 'hand-rankings',
    relatedTerms: ['three_of_a_kind', 'two_pair'],
  },
  flush: {
    term: 'Flush',
    definition: 'Five cards of the same suit, not in sequence.',
    simpleDefinition: '5 cards of the same suit (all hearts, all spades, etc.).',
    visualExample: 'A J 8 4 2',

    level: 'absolute-beginner',
    category: 'hand-rankings',
    relatedTerms: ['flush_draw', 'straight'],
    proTip: 'If two players have flushes, the one with the highest card wins.',
  },
  straight: {
    term: 'Straight',
    definition: 'Five consecutive cards of mixed suits.',
    simpleDefinition: '5 cards in a row (any suits).',
    visualExample: '10 9 8 7 6',

    level: 'absolute-beginner',
    category: 'hand-rankings',
    relatedTerms: ['straight_draw', 'flush'],
    commonMistake: 'The Ace can be high (A-K-Q-J-10) or low (5-4-3-2-A) but NOT wrap around (K-A-2-3-4).',
  },
  three_of_a_kind: {
    term: 'Three of a Kind (Trips/Set)',
    definition: 'Three cards of the same rank.',
    simpleDefinition: '3 cards of the same number/face.',
    visualExample: '9 9 9 K 2',

    level: 'absolute-beginner',
    category: 'hand-rankings',
    relatedTerms: ['full_house', 'two_pair'],
    proTip: 'A "set" is when you have a pair in your hand and hit one on the board. Very disguised!',
  },
  two_pair: {
    term: 'Two Pair',
    definition: 'Two different pairs.',
    simpleDefinition: '2 pairs of different cards.',
    visualExample: 'J J 4 4 A',

    level: 'absolute-beginner',
    category: 'hand-rankings',
    relatedTerms: ['one_pair', 'three_of_a_kind'],
  },
  one_pair: {
    term: 'One Pair',
    definition: 'Two cards of the same rank.',
    simpleDefinition: '2 cards of the same number/face.',
    visualExample: '10 10 K 7 3',

    level: 'absolute-beginner',
    category: 'hand-rankings',
    relatedTerms: ['two_pair', 'high_card', 'kicker'],
  },
  high_card: {
    term: 'High Card',
    definition: 'When you have nothing else, your highest card plays.',
    simpleDefinition: 'No pairs or anything - just your highest card.',
    visualExample: 'A J 8 4 2 = "Ace high"',

    level: 'absolute-beginner',
    category: 'hand-rankings',
    relatedTerms: ['kicker'],
  },
  kicker: {
    term: 'Kicker',
    definition: 'The highest unpaired card that breaks ties between similar hands.',
    simpleDefinition: 'When hands are tied, the highest "extra" card wins!',
    example: 'Both have pair of Kings. You have K-K-A, they have K-K-Q. Your Ace kicker wins!',
    level: 'beginner',
    category: 'hand-rankings',
    relatedTerms: ['one_pair', 'two_pair'],
    proTip: 'This is why A-K is better than A-2. That King kicker matters!',
  },

  // ============================================================================
  // POSITIONS
  // ============================================================================
  position: {
    term: 'Position',
    definition: 'Where you sit relative to the dealer button. The later you act, the more information you have.',
    simpleDefinition: 'Your seat at the table. Acting LAST is a HUGE advantage!',
    example: 'On the Button, you see 5 players act before you. In UTG, you act first with zero info.',
    level: 'beginner',
    category: 'positions',
    relatedTerms: ['button', 'utg', 'cutoff', 'early_position', 'late_position'],
    proTip: 'Position is the MOST important concept in poker. Play tight early, loose late.',
  },
  utg: {
    term: 'UTG (Under the Gun)',
    definition: 'First position to act preflop, directly left of the big blind. Worst position.',
    simpleDefinition: 'First to act - the WORST seat. Everyone sees what you do!',
    level: 'beginner',
    category: 'positions',
    relatedTerms: ['early_position', 'position'],
    proTip: 'Only play your best hands from UTG - maybe top 10% of hands.',
  },
  hijack: {
    term: 'Hijack (HJ)',
    definition: 'Position two seats before the Button. A middle-late position.',
    simpleDefinition: 'The seat 2 spots before the Button.',
    level: 'intermediate',
    category: 'positions',
    relatedTerms: ['cutoff', 'position'],
  },
  cutoff: {
    term: 'Cutoff (CO)',
    definition: 'Position directly to the right of the Button. Second-best position.',
    simpleDefinition: 'The seat right before the Button. Great position!',
    level: 'beginner',
    category: 'positions',
    relatedTerms: ['button', 'position', 'hijack'],
    proTip: 'You can steal blinds frequently from the Cutoff when it folds to you.',
  },
  early_position: {
    term: 'Early Position (EP)',
    definition: 'The first seats to act preflop (UTG, UTG+1). Play tight here.',
    simpleDefinition: 'First seats to act - play only strong hands!',
    level: 'beginner',
    category: 'positions',
    relatedTerms: ['utg', 'position', 'late_position'],
  },
  late_position: {
    term: 'Late Position (LP)',
    definition: 'The last seats to act (Button, Cutoff). Play looser here.',
    simpleDefinition: 'Last seats to act - can play more hands!',
    level: 'beginner',
    category: 'positions',
    relatedTerms: ['button', 'cutoff', 'position'],
  },
  in_position: {
    term: 'In Position (IP)',
    definition: 'Acting AFTER your opponent in each betting round.',
    simpleDefinition: 'You go AFTER the other player - big advantage!',
    level: 'intermediate',
    category: 'positions',
    relatedTerms: ['out_of_position', 'position'],
  },
  out_of_position: {
    term: 'Out of Position (OOP)',
    definition: 'Acting BEFORE your opponent in each betting round.',
    simpleDefinition: 'You have to go FIRST - disadvantage!',
    level: 'intermediate',
    category: 'positions',
    relatedTerms: ['in_position', 'position'],
  },

  // ============================================================================
  // STRATEGY - BEGINNER
  // ============================================================================
  premium_hands: {
    term: 'Premium Hands',
    definition: 'The strongest starting hands: AA, KK, QQ, AK. Always raise with these!',
    simpleDefinition: 'The BEST starting hands. Always play these!',
    visualExample: 'A A, K K, Q Q, A K',

    level: 'beginner',
    category: 'strategy',
    proTip: 'Never fold these preflop. If someone raises, re-raise!',
  },
  drawing_hand: {
    term: 'Drawing Hand',
    definition: 'A hand that needs to improve to win - like 4 cards to a flush or straight.',
    simpleDefinition: 'A hand that\'s not winning YET but could become great.',
    example: 'You have 4 hearts and need 1 more for a flush.',
    level: 'beginner',
    category: 'strategy',
    relatedTerms: ['flush_draw', 'straight_draw', 'outs'],
  },
  flush_draw: {
    term: 'Flush Draw',
    definition: 'Four cards of the same suit, needing one more to complete a flush.',
    simpleDefinition: 'You have 4 of the same suit - one more and you have a flush!',
    visualExample: 'Your hand: A 9, Board: K 7 2',

    example: '9 cards (outs) can give you the flush. About 35% chance by the river.',
    level: 'beginner',
    category: 'strategy',
    relatedTerms: ['outs', 'equity', 'semi_bluff'],
  },
  straight_draw: {
    term: 'Straight Draw',
    definition: 'Four cards to a straight, needing one more to complete it.',
    simpleDefinition: 'You have 4 cards in a row - one more completes the straight!',
    level: 'beginner',
    category: 'strategy',
    relatedTerms: ['open_ended', 'gutshot', 'outs'],
  },
  open_ended: {
    term: 'Open-Ended Straight Draw (OESD)',
    definition: 'A straight draw that can be completed on either end. 8 outs.',
    simpleDefinition: 'A straight draw that can hit on EITHER end - 8 chances!',
    visualExample: 'You have 8-9, board shows 10-J. Either a 7 or Q completes your straight.',
    level: 'beginner',
    category: 'strategy',
    relatedTerms: ['gutshot', 'straight_draw', 'outs'],
  },
  gutshot: {
    term: 'Gutshot (Inside Straight Draw)',
    definition: 'A straight draw needing one specific card in the middle. Only 4 outs.',
    simpleDefinition: 'A straight draw needing ONE specific card - only 4 chances.',
    visualExample: 'You have 8-9, board shows J-Q. Only a 10 completes your straight.',
    level: 'beginner',
    category: 'strategy',
    relatedTerms: ['open_ended', 'outs'],
  },
  bluff: {
    term: 'Bluff',
    definition: 'Betting or raising with a weak hand to make opponents fold better hands.',
    simpleDefinition: 'Betting with a bad hand hoping everyone folds. You win without showing!',
    example: 'You have nothing, but bet big. Opponent folds their pair. You win!',
    level: 'beginner',
    category: 'strategy',
    relatedTerms: ['semi_bluff', 'value_bet'],
    commonMistake: 'Bluffing too much. Save bluffs for good spots!',
  },
  value_bet: {
    term: 'Value Bet',
    definition: 'Betting with a strong hand to get called by weaker hands.',
    simpleDefinition: 'Betting because you have a good hand and want to get paid!',
    example: 'You have top pair. You bet hoping weaker pairs or draws call.',
    level: 'beginner',
    category: 'strategy',
    relatedTerms: ['bluff', 'thin_value'],
    proTip: 'Always bet your strong hands for value - don\'t slow play too much!',
  },
  slow_play: {
    term: 'Slow Play',
    definition: 'Playing a strong hand passively (checking/calling) to disguise its strength.',
    simpleDefinition: 'Pretending you\'re weak when you\'re actually strong.',
    example: 'You flop a full house but just check, hoping opponent bets.',
    level: 'intermediate',
    category: 'strategy',
    relatedTerms: ['check_raise', 'trap'],
    commonMistake: 'Slow playing too often and letting opponents catch up for free.',
  },
  semi_bluff: {
    term: 'Semi-Bluff',
    definition: 'Betting with a drawing hand that could improve. You can win if they fold OR if you hit.',
    simpleDefinition: 'Bluffing with a hand that could still become the best hand.',
    example: 'You have a flush draw. You bet. They might fold (win now!) or you might hit your flush (win later!).',
    level: 'intermediate',
    category: 'strategy',
    relatedTerms: ['bluff', 'flush_draw', 'equity'],
    proTip: 'Semi-bluffs are great because you have two ways to win!',
  },

  // ============================================================================
  // STATS & MATH
  // ============================================================================
  vpip: {
    term: 'VPIP (Voluntarily Put $ In Pot)',
    definition: 'The percentage of hands where you voluntarily put money in the pot (call or raise).',
    simpleDefinition: 'How many hands you play (not counting blinds you\'re forced to pay).',
    example: 'If you play 25 out of 100 hands, your VPIP is 25%. Good is 18-25%.',
    level: 'beginner',
    category: 'stats',
    proTip: 'High VPIP = loose player (plays many hands). Low = tight (plays few).',
  },
  pfr: {
    term: 'PFR (Pre-Flop Raise)',
    definition: 'The percentage of hands where you raise preflop.',
    simpleDefinition: 'How often you raise before the flop.',
    example: 'If you raise 18 out of 100 hands preflop, your PFR is 18%.',
    level: 'beginner',
    category: 'stats',
    relatedTerms: ['vpip'],
    proTip: 'Your PFR should be close to your VPIP. Big gap = too much calling.',
  },
  cbet: {
    term: 'C-Bet (Continuation Bet)',
    definition: 'Betting the flop after you raised preflop, continuing your aggression.',
    simpleDefinition: 'You raised before the flop, then bet again on the flop.',
    example: 'You raise with AK, flop comes 7-2-4. You bet anyway.',
    level: 'beginner',
    category: 'stats',
    proTip: 'C-bet about 50-70% of flops. Don\'t c-bet EVERY time.',
  },
  aggFactor: {
    term: 'Aggression Factor (AF)',
    definition: 'Ratio of aggressive actions (bet/raise) to passive actions (call).',
    simpleDefinition: 'How much you bet/raise compared to how much you call.',
    example: 'AF of 2.5 means you bet/raise 2.5x more than you call. Good is 2-3.',
    level: 'intermediate',
    category: 'stats',
    proTip: 'Higher aggression usually = more profits. But don\'t be a maniac!',
  },
  wtsd: {
    term: 'WTSD (Went to Showdown)',
    definition: 'Percentage of hands where you reached showdown after seeing the flop.',
    simpleDefinition: 'How often you see the final showdown.',
    level: 'intermediate',
    category: 'stats',
  },
  outs: {
    term: 'Outs',
    definition: 'Cards remaining in the deck that will improve your hand to (likely) the winner.',
    simpleDefinition: 'Cards left that can help you win!',
    example: 'Flush draw = 9 outs (9 cards of your suit remain). Straight draw = 8 outs.',
    level: 'beginner',
    category: 'stats',
    relatedTerms: ['equity', 'pot_odds'],
    proTip: 'Rule of 4 and 2: Multiply outs by 4 on flop (for turn+river), by 2 on turn.',
  },
  pot_odds: {
    term: 'Pot Odds',
    definition: 'The ratio of the pot size to the cost of calling. Helps decide if a call is profitable.',
    simpleDefinition: 'Is it worth calling based on the pot size vs what you have to pay?',
    example: 'Pot is $100, call is $20. You get 5:1 odds (need 16.7% to win to break even).',
    level: 'intermediate',
    category: 'stats',
    relatedTerms: ['outs', 'equity', 'implied_odds'],
    proTip: 'If your chance to win > what pot odds require, CALL. Otherwise FOLD.',
  },
  equity: {
    term: 'Equity',
    definition: 'Your percentage chance of winning the hand.',
    simpleDefinition: 'Your odds of winning if all cards are dealt out.',
    example: 'Flush draw on the flop = about 35% equity (35% to win).',
    level: 'intermediate',
    category: 'stats',
    relatedTerms: ['outs', 'pot_odds'],
  },
  implied_odds: {
    term: 'Implied Odds',
    definition: 'Expected future winnings if you hit your draw, beyond just the current pot.',
    simpleDefinition: 'Extra money you expect to win AFTER you hit your hand.',
    example: 'Pot odds say fold, but if they\'ll pay you off big when you hit, implied odds justify calling.',
    level: 'advanced',
    category: 'stats',
    relatedTerms: ['pot_odds'],
  },

  // ============================================================================
  // ADVANCED STRATEGY
  // ============================================================================
  gto: {
    term: 'GTO (Game Theory Optimal)',
    definition: 'A balanced strategy that cannot be exploited no matter how an opponent plays, mixing value bets and bluffs at the right frequencies. Solvers compute it; the preflop charts in Training are a simplified version of what they recommend.',
    simpleDefinition: 'The "textbook correct" way to play so nobody can take advantage of you. Our preflop charts show the GTO play for every starting hand.',
    example: 'From the button with nobody raised, GTO says raise about 43% of hands. From under the gun at a 6-max table, 17%.',
    level: 'intermediate',
    category: 'strategy',
    relatedTerms: ['range', 'push_fold', 'position', 'premium_hands'],
    proTip: 'Open the Charts button on any training screen. Learn the chart for your seat first, then adjust to how the people at your table actually play.',
  },
  range: {
    term: 'Range',
    definition: 'All the starting hands a player would play the same way in a given spot. Good players think about ranges, not one exact hand.',
    simpleDefinition: 'Instead of guessing one hand, think "which hands would they do this with?" That whole group is their range.',
    example: 'A tight player who raises from under the gun probably has a range like pairs 7-7 and up, A-Q, A-K and a few suited aces.',
    level: 'intermediate',
    category: 'strategy',
    relatedTerms: ['gto', 'position', 'premium_hands'],
    proTip: 'The preflop chart for each seat IS a range. The colored squares are the hands you play, the dark ones you fold.',
  },
  mdf: {
    term: 'MDF (Minimum Defense Frequency)',
    definition: 'The minimum % of hands you must continue with to prevent opponent from profitably bluffing any two cards.',
    simpleDefinition: 'How often you need to call to stop them from bluffing everything.',
    example: 'Vs pot-sized bet, MDF is 50% - you must defend at least half your range.',
    level: 'advanced',
    category: 'strategy',
  },
  spr: {
    term: 'SPR (Stack-to-Pot Ratio)',
    definition: 'Effective stack divided by pot size. Determines commitment level.',
    simpleDefinition: 'How deep the stacks are compared to the pot.',
    example: 'SPR of 3 with top pair = usually committed. SPR of 10 = can fold to aggression.',
    level: 'advanced',
    category: 'strategy',
  },
  thin_value: {
    term: 'Thin Value',
    definition: 'Betting a medium-strength hand for value that only slightly beats the hands that will call.',
    simpleDefinition: 'Betting a medium hand hoping even weaker hands call.',
    level: 'advanced',
    category: 'strategy',
    relatedTerms: ['value_bet'],
  },
  pot_control: {
    term: 'Pot Control',
    definition: 'Playing passively with medium-strength hands to keep the pot small.',
    simpleDefinition: 'Not betting too much when you\'re not sure you have the best hand.',
    level: 'intermediate',
    category: 'strategy',
  },
  board_texture: {
    term: 'Board Texture',
    definition: 'How the community cards work together. Dry = few draws. Wet = many draws.',
    simpleDefinition: 'Is the board "scary" (lots of draws) or "safe" (few draws)?',
    example: 'K 7 2 is DRY (safe). J T 9 is WET (scary, many straights/flushes).',

    level: 'intermediate',
    category: 'strategy',
    proTip: 'C-bet more on dry boards, check more on wet boards.',
  },

  // ============================================================================
  // PLAYER TYPES
  // ============================================================================
  tight: {
    term: 'Tight',
    definition: 'Playing few hands, only strong holdings.',
    simpleDefinition: 'Only plays good hands. Folds a lot.',
    level: 'beginner',
    category: 'player-types',
    relatedTerms: ['loose', 'tag', 'nit'],
  },
  loose: {
    term: 'Loose',
    definition: 'Playing many hands, including weak ones.',
    simpleDefinition: 'Plays lots of hands, even bad ones.',
    level: 'beginner',
    category: 'player-types',
    relatedTerms: ['tight', 'lag', 'fish'],
  },
  aggressive: {
    term: 'Aggressive',
    definition: 'Betting and raising frequently.',
    simpleDefinition: 'Bets and raises a lot.',
    level: 'beginner',
    category: 'player-types',
    relatedTerms: ['passive', 'lag', 'tag'],
  },
  passive: {
    term: 'Passive',
    definition: 'Checking and calling frequently, rarely betting or raising.',
    simpleDefinition: 'Checks and calls a lot. Rarely bets big.',
    level: 'beginner',
    category: 'player-types',
    relatedTerms: ['aggressive', 'calling_station'],
  },
  tag: {
    term: 'TAG (Tight-Aggressive)',
    definition: 'Plays few hands but plays them aggressively. The winning style!',
    simpleDefinition: 'Only plays good hands, but bets them hard.',
    level: 'beginner',
    category: 'player-types',
    relatedTerms: ['lag', 'tight', 'aggressive'],
    proTip: 'This is the style most winning players use!',
  },
  lag: {
    term: 'LAG (Loose-Aggressive)',
    definition: 'Plays many hands and plays them aggressively. High variance style.',
    simpleDefinition: 'Plays lots of hands and bets them hard.',
    level: 'intermediate',
    category: 'player-types',
    relatedTerms: ['tag', 'loose', 'aggressive'],
  },
  nit: {
    term: 'Nit',
    definition: 'Extremely tight player who only plays premium hands. Easy to exploit.',
    simpleDefinition: 'Super tight - only plays the very best hands.',
    level: 'beginner',
    category: 'player-types',
    relatedTerms: ['tight', 'rock'],
    proTip: 'Against nits: fold when they bet, steal their blinds.',
  },
  fish: {
    term: 'Fish',
    definition: 'A weak, losing player. Usually too loose and passive.',
    simpleDefinition: 'A bad player who loses money.',
    level: 'beginner',
    category: 'player-types',
    relatedTerms: ['shark', 'calling_station'],
  },
  calling_station: {
    term: 'Calling Station',
    definition: 'A player who calls too much and rarely folds or raises.',
    simpleDefinition: 'Someone who calls everything but never raises.',
    level: 'beginner',
    category: 'player-types',
    relatedTerms: ['passive', 'fish'],
    proTip: 'Against calling stations: never bluff! Just value bet your good hands.',
  },
  shark: {
    term: 'Shark',
    definition: 'A skilled, winning player.',
    simpleDefinition: 'A good player who wins money.',
    level: 'beginner',
    category: 'player-types',
    relatedTerms: ['fish'],
  },
  tilt: {
    term: 'Tilt',
    definition: 'Emotional state causing poor decisions, usually after bad beats.',
    simpleDefinition: 'Playing badly because you\'re angry or frustrated.',
    example: 'You lost with AA to a bad player. Now you\'re angry and playing every hand.',
    level: 'beginner',
    category: 'player-types',
    proTip: 'Recognize when you\'re tilting and take a break!',
  },

  // ============================================================================
  // MISCELLANEOUS
  // ============================================================================
  bankroll: {
    term: 'Bankroll',
    definition: 'The total money you have set aside for playing poker.',
    simpleDefinition: 'Your poker money - separate from living expenses.',
    level: 'beginner',
    category: 'basics',
    proTip: 'Have at least 20-30 buy-ins for the stakes you play.',
  },
  variance: {
    term: 'Variance',
    definition: 'The natural swings in results due to luck in the short term.',
    simpleDefinition: 'The luck factor - winning and losing streaks are normal!',
    level: 'intermediate',
    category: 'basics',
    proTip: 'Even great players have losing months. Trust the process.',
  },
  icm: {
    term: 'ICM (Independent Chip Model)',
    definition: 'A mathematical model that converts tournament chip stacks into real-money equity. Chips are worth less the more you have, so risking your stack in a tournament is costlier than in a cash game.',
    simpleDefinition: 'In tournaments, your chips are worth more when you have fewer of them. Losing all your chips is worse than doubling up is good.',
    example: 'You\'re on the bubble with a big stack. Even with a decent hand, you might fold because busting now means $0, while just surviving guarantees prize money.',
    level: 'advanced',
    category: 'strategy',
    relatedTerms: ['expected_value', 'pot_odds'],
    commonMistake: 'Playing tournament hands the same way you\'d play cash game hands. In tournaments, survival matters more than maximizing chips.',
    proTip: 'ICM pressure is highest on the bubble and at final table pay jumps. Use your big stack to pressure medium stacks who can\'t afford to bust.',
  },
  expected_value: {
    term: 'Expected Value (EV)',
    definition: 'The average amount you expect to win or lose on a play over many repetitions.',
    simpleDefinition: 'Is this play profitable in the long run?',
    example: '+EV means profitable over time. -EV means losing over time.',
    level: 'intermediate',
    category: 'basics',
  },
  bad_beat: {
    term: 'Bad Beat',
    definition: 'Losing a hand despite being a big favorite.',
    simpleDefinition: 'Losing when you should have won - unlucky!',
    example: 'You have AA, opponent has 22. They hit a 2. Bad beat!',
    level: 'beginner',
    category: 'basics',
    proTip: 'Bad beats happen. Focus on making good decisions, not results.',
  },
  cooler: {
    term: 'Cooler',
    definition: 'A hand where both players have very strong hands and losing is unavoidable.',
    simpleDefinition: 'When both players have monster hands and someone has to lose.',
    example: 'You have KK, opponent has AA. Very hard to fold!',
    level: 'intermediate',
    category: 'basics',
  },

  // ============================================================================
  // POKER JARGON - BEGINNER FRIENDLY EXPLANATIONS
  // ============================================================================
  villain: {
    term: 'Villain',
    definition: 'Poker slang for your opponent in a hand. Not actually a bad person - just the player you\'re competing against!',
    simpleDefinition: 'The other player(s) you\'re playing against. It\'s just poker slang!',
    example: '"Villain bets $50" means your opponent bet $50.',
    level: 'absolute-beginner',
    category: 'basics',
    relatedTerms: ['hero', 'opponent'],
    proTip: 'In hand discussions, "villain" = opponent, "hero" = you. It\'s not about good vs evil!',
  },
  hero: {
    term: 'Hero',
    definition: 'Poker slang for YOU - the player whose perspective we\'re analyzing.',
    simpleDefinition: 'You! When someone says "hero has AK", they mean you have AK.',
    example: '"Hero raises to $15" means you raised to $15.',
    level: 'absolute-beginner',
    category: 'basics',
    relatedTerms: ['villain'],
  },
  action: {
    term: 'Action',
    definition: 'Either betting/raising activity, or whose turn it is to act.',
    simpleDefinition: '"The action is on you" means it\'s your turn. "Lots of action" means lots of betting.',
    example: '"Action is on the button" means it\'s the button\'s turn to act.',
    level: 'absolute-beginner',
    category: 'basics',
  },
  board: {
    term: 'Board',
    definition: 'The community cards in the middle of the table that all players share.',
    simpleDefinition: 'The 5 cards in the middle everyone uses to make their hand.',
    example: 'The board is K Q 7 2 9 - everyone combines these with their hole cards.',

    level: 'absolute-beginner',
    category: 'basics',
    relatedTerms: ['flop', 'turn', 'river', 'community_cards'],
  },
  nuts: {
    term: 'The Nuts',
    definition: 'The best possible hand given the current board.',
    simpleDefinition: 'The absolute best hand anyone could have right now.',
    example: 'Board is A K Q J 2. The nuts is T for a royal flush.',

    level: 'beginner',
    category: 'strategy',
    proTip: 'Always know what the nuts is on each board!',
  },
  air: {
    term: 'Air',
    definition: 'A hand with no made hand and no draw - essentially nothing.',
    simpleDefinition: 'You have nothing! No pair, no draw, just air.',
    example: 'You have 7 2 on a K Q 9 4 A board. That\'s air.',

    level: 'beginner',
    category: 'strategy',
  },
  monster: {
    term: 'Monster',
    definition: 'A very strong hand - usually the nuts or close to it.',
    simpleDefinition: 'A super strong hand! Like a full house or better.',
    example: 'You flopped a set of Aces. That\'s a monster!',
    level: 'beginner',
    category: 'strategy',
  },
  cracked: {
    term: 'Cracked',
    definition: 'When a strong hand (usually AA or KK) loses.',
    simpleDefinition: 'When your Aces or Kings lose. Painful!',
    example: '"My Aces got cracked by 72 offsuit" - they hit two pair.',
    level: 'beginner',
    category: 'basics',
  },
  brick: {
    term: 'Brick',
    definition: 'A card that doesn\'t help anyone or complete any draws.',
    simpleDefinition: 'A useless card that doesn\'t change anything.',
    example: 'Board is K Q J with flush draws. River 2 is a brick.',

    level: 'beginner',
    category: 'basics',
  },
  runner_runner: {
    term: 'Runner-Runner',
    definition: 'Needing both the turn AND river cards to complete your hand.',
    simpleDefinition: 'Needing two perfect cards in a row to win. Very unlikely!',
    example: 'You need two more hearts for a flush. That\'s runner-runner.',
    level: 'beginner',
    category: 'basics',
    proTip: 'Runner-runner draws are usually too weak to chase.',
  },
  ship_it: {
    term: 'Ship It',
    definition: 'Slang for going all-in or winning a pot.',
    simpleDefinition: '"Ship it!" = Go all-in or "I won!"',
    example: '"Ship it in the middle" means go all-in.',
    level: 'beginner',
    category: 'basics',
  },
  muck: {
    term: 'Muck',
    definition: 'To fold or discard your cards without showing them.',
    simpleDefinition: 'Throw away your cards face-down. No one sees what you had.',
    example: 'You lost but don\'t want to show your bluff, so you muck.',
    level: 'beginner',
    category: 'actions',
  },
  slow_roll: {
    term: 'Slow Roll',
    definition: 'Taking a long time to show a winning hand at showdown. Considered very rude!',
    simpleDefinition: 'Pretending you might lose when you have the winner. Don\'t do this!',
    example: 'You have the nuts but act sad before revealing. Very bad etiquette.',
    level: 'beginner',
    category: 'basics',
    proTip: 'Never slow roll - it\'s the most disrespectful thing in poker.',
  },
  donkey: {
    term: 'Donkey (Donk)',
    definition: 'A weak or inexperienced player. Also "donk bet" means betting into the preflop raiser.',
    simpleDefinition: 'A bad player OR a bet into the person who raised last.',
    example: '"He\'s such a donkey" = bad player. "He donk bet" = bet into the raiser.',
    level: 'beginner',
    category: 'player-types',
  },
  whale: {
    term: 'Whale',
    definition: 'A wealthy recreational player who plays high stakes and loses a lot.',
    simpleDefinition: 'A rich player who loses lots of money. Everyone wants whales at their table!',
    level: 'beginner',
    category: 'player-types',
  },
  reg: {
    term: 'Reg (Regular)',
    definition: 'A player who plays regularly at the same stakes, usually a winning player.',
    simpleDefinition: 'Someone who plays often at these stakes. Usually knows what they\'re doing.',
    level: 'intermediate',
    category: 'player-types',
  },
  spewy: {
    term: 'Spewy',
    definition: 'Making overly aggressive or loose plays that lose money.',
    simpleDefinition: 'Playing too wild and throwing away chips.',
    example: 'Bluffing every hand is spewy.',
    level: 'beginner',
    category: 'strategy',
  },
  standard: {
    term: 'Standard',
    definition: 'A commonly accepted, conventional play.',
    simpleDefinition: 'The normal, expected play most people would make.',
    example: '"Standard open to 3x" means a normal raise size.',
    level: 'beginner',
    category: 'strategy',
  },
  snap: {
    term: 'Snap',
    definition: 'To make a decision instantly without thinking.',
    simpleDefinition: 'Deciding immediately - "snap call" = call right away.',
    example: '"I snap called with the nuts" = instantly called.',
    level: 'beginner',
    category: 'actions',
  },
  tank: {
    term: 'Tank',
    definition: 'To take a long time thinking about a decision.',
    simpleDefinition: 'Thinking for a while before acting.',
    example: '"He tanked for 2 minutes before folding."',
    level: 'beginner',
    category: 'actions',
  },
  dirty: {
    term: 'Dirty',
    definition: 'A card or board that could have helped opponents.',
    simpleDefinition: 'A scary card that might have helped the other player.',
    example: 'The river 4 looks dirty - it could complete a straight.',
    level: 'beginner',
    category: 'basics',
  },
  clean: {
    term: 'Clean',
    definition: 'A card that doesn\'t help opponents or change the situation.',
    simpleDefinition: 'A safe card that doesn\'t change anything.',
    example: 'The river 2 is clean - no draws completed.',
    level: 'beginner',
    category: 'basics',
  },
  way_ahead_way_behind: {
    term: 'Way Ahead/Way Behind',
    definition: 'A situation where you\'re either crushing your opponent or being crushed - no middle ground.',
    simpleDefinition: 'You\'re either winning big or losing big. Not much in between.',
    example: 'With AA on K72 rainbow, you\'re either way ahead (vs KQ) or way behind (vs KK).',
    level: 'intermediate',
    category: 'strategy',
    proTip: 'In WAWB spots, pot control is often best.',
  },

  // ============================================================================
  // POKERSWEEPSTARS PLATFORM TERMS
  // ============================================================================
  pkcash: {
    term: 'PSS Coins',
    definition: 'PokerSweepStars premium currency that can be redeemed for real money.',
    simpleDefinition: 'Real money currency! Win it, earn it, withdraw it.',
    example: 'You have $50 PSS Coins in your wallet. You can redeem it or play with it.',
    level: 'absolute-beginner',
    category: 'basics',
    proTip: 'PSS Coins tables are prize-eligible - play within your bankroll!',
  },
  playcoins: {
    term: 'PlayCoins',
    definition: 'Free play currency for practice. Cannot be withdrawn but great for learning!',
    simpleDefinition: 'Free chips for practice! Can\'t cash out but perfect for learning.',
    example: 'You get 1,000 PlayCoins free. Use them to practice without risking real money.',
    level: 'absolute-beginner',
    category: 'basics',
    proTip: 'New to poker? Start with PlayCoins until you\'re comfortable.',
  },
  playthrough: {
    term: 'Playthrough',
    definition: 'The amount you must wager before withdrawing bonus funds or winnings.',
    simpleDefinition: 'You need to play a certain amount before you can withdraw bonuses.',
    example: '5x playthrough on $10 bonus = play $50 in hands before withdrawing.',
    level: 'beginner',
    category: 'basics',
  },
  training_mode: {
    term: 'Training Mode',
    definition: 'Practice poker against AI bots with coaching tips and earn PSS Coins rewards.',
    simpleDefinition: 'Practice against computer opponents and earn rewards while learning!',
    example: 'Complete training scenarios to earn PSS Coins and improve your skills.',
    level: 'absolute-beginner',
    category: 'basics',
    proTip: 'Complete daily training challenges for bonus PSS Coins!',
  },
  daily_challenge: {
    term: 'Daily Challenge',
    definition: '5 training scenarios per day with bonus rewards for completion.',
    simpleDefinition: 'Complete 5 scenarios daily for extra PSS Coins rewards!',
    example: 'Get all 5 daily challenges correct for a $0.25 PSS Coins bonus.',
    level: 'absolute-beginner',
    category: 'basics',
  },
  training_streak: {
    term: 'Training Streak',
    definition: 'Consecutive days of completing training. Longer streaks = bigger rewards!',
    simpleDefinition: 'Train every day for multiplied rewards! 7 days = 50% bonus.',
    example: '30-day streak = 2x PSS Coins on all training rewards.',
    level: 'beginner',
    category: 'basics',
  },
  agent: {
    term: 'Agent',
    definition: 'A referral partner who earns commission when their referred players play.',
    simpleDefinition: 'Refer friends and earn a % of the rake they generate!',
    example: 'As an agent, you earn 10% to 20% of the rake from players you referred, depending on your tier.',
    level: 'intermediate',
    category: 'basics',
  },
  rake_poker: {
    term: 'Rake',
    definition: 'The small fee the house takes from each pot (usually 2.5-5%).',
    simpleDefinition: 'The house takes a tiny cut of each pot. That\'s how poker rooms make money.',
    example: 'Pot is $100, rake is 5% = house takes $5.',
    level: 'beginner',
    category: 'structure',
  },
  kyc: {
    term: 'KYC',
    definition: 'Know Your Customer - identity verification required for withdrawals.',
    simpleDefinition: 'Verify your identity (ID upload) to withdraw. Keeps everyone safe!',
    example: 'Upload your ID and selfie to complete KYC and enable withdrawals.',
    level: 'beginner',
    category: 'basics',
  },
  leaderboard_platform: {
    term: 'Leaderboard',
    definition: 'Rankings showing top players by hands won, weekly/monthly competitions.',
    simpleDefinition: 'See who\'s winning the most! Weekly prizes for top players.',
    example: 'Win hands to climb the leaderboard and earn bonus PSS Coins.',
    level: 'absolute-beginner',
    category: 'basics',
  },
  challenge_platform: {
    term: 'Monthly Challenge',
    definition: 'Monthly competitions with rotating goals (hands won, biggest pot, etc.) and prize pools.',
    simpleDefinition: 'Monthly contests with real prizes! Different challenge each month.',
    example: 'January: Hands Champion - win the most hands for $50+ in prizes.',
    level: 'beginner',
    category: 'basics',
  },

  // ============================================================================
  // TOURNAMENT TERMS - vocabulary for the Tournament Training section
  // ============================================================================
  bubble: {
    term: 'Bubble',
    definition: 'The stage of a tournament just before the money: one or two more eliminations and everyone remaining gets paid.',
    simpleDefinition: 'The nail-biting moment right before prize money starts. Bust now and you win nothing - survive one more elimination and you\'re paid!',
    example: '46 players left, 45 get paid - you are "on the bubble". The next player out leaves with nothing.',
    level: 'beginner',
    category: 'tournament',
    relatedTerms: ['icm', 'min_cash', 'pay_jump'],
    commonMistake: 'Calling all-ins with decent-but-not-great hands on the bubble. Surviving is worth more than winning chips here.',
    proTip: 'If you have a big stack, raise MORE on the bubble - everyone else is scared to play back at you.',
  },
  pay_jump: {
    term: 'Pay Jump (Laddering)',
    definition: 'The increase in prize money between finishing positions. "Laddering" means surviving long enough to climb to the next payout level.',
    simpleDefinition: 'Every player who busts before you = more money for you. Climbing the payout ladder one bust at a time.',
    example: '5th place pays $50, 4th pays $80. If a shorter stack is about to bust, folding almost everything until they do earns you $30 for free.',
    level: 'beginner',
    category: 'tournament',
    relatedTerms: ['bubble', 'icm', 'final_table'],
    commonMistake: 'Ignoring the shortest stack at the table. If someone is nearly out, let THEM bust first before taking risks.',
    proTip: 'Pay jumps are biggest at the final table - the jump from 2nd to 1st is usually the largest of all.',
  },
  push_fold: {
    term: 'Push/Fold',
    definition: 'A short-stack strategy where your only preflop options are all-in (push) or fold - no small raises or calls.',
    simpleDefinition: 'When your stack gets tiny (under ~10 big blinds), stop raising small. Either shove everything in or throw the hand away.',
    example: 'At 8 big blinds on the button with K7, the right play is all-in - not a small raise you\'d have to fold.',
    level: 'beginner',
    category: 'tournament',
    relatedTerms: ['jam', 'fold_equity', 'effective_stack', 'gto', 'range'],
    commonMistake: 'Raising small with a short stack, then folding to a re-raise. You just donated chips you couldn\'t afford.',
    proTip: 'Push/fold ranges depend on your seat: the same hand can be a clear shove from the button and a clear fold from early position. The Charts button switches to the short-stack chart on its own once your stack is 20 big blinds or less.',
  },
  jam: {
    term: 'Jam (Shove)',
    definition: 'Going all-in, usually as a raise. "Open-jam" means going all-in as the first raiser; "rejam" means going all-in over someone else\'s raise.',
    simpleDefinition: 'Pushing ALL your chips in at once. Also called shoving.',
    example: 'Everyone folds to you at 9 big blinds - you jam (all-in) with A5 suited.',
    level: 'beginner',
    category: 'tournament',
    relatedTerms: ['push_fold', 'rejam', 'all_in'],
    proTip: 'A jam wins two ways: everyone folds, or you win the showdown. That double chance is what makes short-stack jams so powerful.',
  },
  rejam: {
    term: 'Rejam (Resteal)',
    definition: 'Going all-in over an opponent\'s raise, typically with 8-16 big blinds, targeting raises that were likely steals.',
    simpleDefinition: 'Someone raises trying to steal the blinds - you shove all-in right over the top and steal from the stealer!',
    example: 'The cutoff min-raises for the fifth time. You jam 14 big blinds from the big blind with K9 suited - they fold and you collect the pot.',
    level: 'intermediate',
    category: 'tournament',
    relatedTerms: ['jam', 'steal', 'blocker'],
    commonMistake: 'Rejamming into tight players who only raise strong hands. Resteal against frequent raisers, not rocks.',
    proTip: 'Hands with an ace or king make the best rejams - your blocker makes it less likely they have a calling hand.',
  },
  squeeze_play: {
    term: 'Squeeze Play',
    definition: 'A big re-raise after one player raises and another calls - pressuring both with the dead money already in the pot.',
    simpleDefinition: 'One player raises, another calls, and you re-raise big. The caller is "squeezed" - they showed weakness by not raising, and now face a huge bet.',
    example: 'Cutoff opens, button flat-calls, and you jam from the big blind with KQ. Both fold and you win 5+ big blinds without a flop.',
    level: 'intermediate',
    category: 'tournament',
    relatedTerms: ['three_bet', 'dead_money', 'steal'],
    commonMistake: 'Squeezing when the caller is a short stack - short-stack flat-calls are often traps with monster hands.',
    proTip: 'The best squeeze targets: a frequent raiser plus a passive caller. Both ranges are weak and the pot is fat.',
  },
  steal: {
    term: 'Steal',
    definition: 'Raising from late position (cutoff, button, small blind) primarily to win the blinds and antes uncontested.',
    simpleDefinition: 'Raising with almost any two cards when everyone has folded to you, hoping the blinds just give up.',
    example: 'Folded to you on the button with 97 suited - you min-raise. Both blinds fold and you profit without seeing a flop.',
    level: 'beginner',
    category: 'tournament',
    relatedTerms: ['rejam', 'button', 'ante'],
    commonMistake: 'Never stealing - or stealing with garbage into players who always fight back.',
    proTip: 'Under 40 big blinds with antes, successful steals grow your stack meaningfully every single orbit. It\'s the engine of tournament survival.',
  },
  blocker: {
    term: 'Blocker',
    definition: 'A card in your hand that reduces the number of strong hands your opponent can have.',
    simpleDefinition: 'Holding an ace means it\'s harder for your opponent to have aces - you\'re holding one of them!',
    example: 'You jam A4. Even if called, your ace "blocks" AA and AK - there are fewer combos of hands that crush you.',
    level: 'intermediate',
    category: 'strategy',
    relatedTerms: ['rejam', 'range', 'bluff'],
    proTip: 'Suited wheel aces (A2s-A5s) make great bluff-jams: the ace blocks calls, and the hand still has playable equity when called.',
  },
  fold_equity: {
    term: 'Fold Equity',
    definition: 'The portion of a bet\'s profitability that comes from the chance your opponent folds.',
    simpleDefinition: 'Money you win because they give up - not because you had the best hand.',
    example: 'Your all-in gets folds 40% of the time. That 40% is pure profit before your cards even matter.',
    level: 'intermediate',
    category: 'strategy',
    relatedTerms: ['jam', 'semi_bluff', 'bluff'],
    commonMistake: 'Jamming into players who never fold. No folds = no fold equity = you need the best hand.',
    proTip: 'Fold equity shrinks as your stack shrinks - a 3bb shove scares nobody. Act before your stack stops being a threat.',
  },
  dead_money: {
    term: 'Dead Money',
    definition: 'Chips already in the pot from players unlikely to fight for it - blinds, antes, and abandoned bets.',
    simpleDefinition: 'Free chips sitting in the middle that nobody is defending. Whoever wins the pot gets them as a bonus.',
    example: 'With blinds and antes, every pot starts with 2+ big blinds of dead money - that\'s why stealing works.',
    level: 'intermediate',
    category: 'strategy',
    relatedTerms: ['steal', 'squeeze_play', 'ante'],
    proTip: 'Count the dead money before bluffing: the more there is, the less often your bluff needs to work to profit.',
  },
  effective_stack: {
    term: 'Effective Stack',
    definition: 'The smaller of the stacks in a hand - the maximum that can actually be won or lost.',
    simpleDefinition: 'You can only win what the shorter stack has. If you have 100bb and they have 20bb, you\'re playing a 20bb hand.',
    example: 'You cover the table with 80bb, but the all-in player has 12bb - only 12bb of yours are at risk.',
    level: 'intermediate',
    category: 'tournament',
    relatedTerms: ['stack', 'push_fold', 'coverage'],
    commonMistake: 'Playing deep-stack poker (set mining, speculative calls) when the OTHER player\'s short stack makes those plays unprofitable.',
    proTip: 'Always think in effective stacks: your strategy should match the smaller stack in the hand, not your own.',
  },
  blind_levels: {
    term: 'Blind Levels',
    definition: 'Tournament blinds increase on a schedule (levels). Each level raises the blinds/antes, shrinking everyone\'s stack in big-blind terms.',
    simpleDefinition: 'The forced bets get bigger every few minutes. Your chips stay the same, but they buy fewer big blinds every level!',
    example: 'You have 6,000 chips. At 100/200 that\'s 30 big blinds - comfortable. Two levels later at 300/600 it\'s 10 - panic territory.',
    level: 'beginner',
    category: 'tournament',
    relatedTerms: ['blinds', 'ante', 'effective_stack'],
    commonMistake: 'Ignoring the clock. A "fine" stack becomes a short stack at the next level - plan one level ahead.',
    proTip: 'Check the blind schedule before playing: fast (turbo) structures demand earlier aggression than slow ones.',
  },
  late_registration: {
    term: 'Late Registration',
    definition: 'A window after a tournament starts during which new players can still enter, usually with the starting stack.',
    simpleDefinition: 'Missed the start? You can still join for a while - you\'ll get the normal starting chips, but the blinds are already bigger.',
    example: 'Late reg is open through level 4. Joining at level 4 gets you 100bb worth of chips but at bigger blinds - effectively a shorter stack.',
    level: 'beginner',
    category: 'tournament',
    relatedTerms: ['blind_levels', 're_entry'],
    proTip: 'Entering late means fewer hands against weak players who\'ve already busted - but a shorter effective stack. Pros disagree on which matters more!',
  },
  re_entry: {
    term: 'Re-Entry / Rebuy',
    definition: 'Paying the buy-in again after busting to receive a fresh starting stack, allowed during a set window.',
    simpleDefinition: 'Busted early? Pay the entry fee again and jump back in with fresh chips (only while the re-entry window is open).',
    example: 'You bust in level 3 of a rebuy event, pay the $10 again, and return with a full stack. The prize pool grows with every re-entry.',
    level: 'beginner',
    category: 'tournament',
    relatedTerms: ['late_registration', 'add_on'],
    commonMistake: 'Playing recklessly "because I can just rebuy". Every re-entry is real money - two buy-ins need twice the prize to break even.',
    proTip: 'Re-entries feed the prize pool - fields with heavy rebuying often have bigger prizes than the entry count suggests.',
  },
  add_on: {
    term: 'Add-On',
    definition: 'A one-time optional chip purchase offered at the end of the rebuy period, available to all players regardless of stack.',
    simpleDefinition: 'A one-time offer to buy extra chips - even if you never busted. Usually happens when the rebuy window closes.',
    example: 'At the first break, everyone may buy 5,000 more chips for $5 - one time only.',
    level: 'beginner',
    category: 'tournament',
    relatedTerms: ['re_entry', 'blind_levels'],
    proTip: 'Add-ons are usually great value chip-for-chip - taking it is standard unless your stack is already massive.',
  },
  final_table: {
    term: 'Final Table',
    definition: 'The last table of a tournament - typically the final 6-9 players, where the biggest prizes and pay jumps live.',
    simpleDefinition: 'The last group of players standing. The money gets serious here - every single bust-out raises your prize.',
    example: 'You reach the final table 8th of 9 in chips. Careful play to outlast two players earns two pay jumps before you\'re even at risk.',
    level: 'beginner',
    category: 'tournament',
    relatedTerms: ['pay_jump', 'icm', 'bubble'],
    proTip: 'Final table ICM is extreme: avoid big-stack-vs-big-stack wars, and pressure the medium stacks that fear busting most.',
  },
  min_cash: {
    term: 'Min-Cash',
    definition: 'The smallest prize in a tournament - what you earn for barely making the money.',
    simpleDefinition: 'Sneaking into the prize money at the lowest payout, usually about 1.5-2x your entry fee.',
    example: '45 players get paid; you finish 44th and min-cash for $18 on a $10 entry.',
    level: 'beginner',
    category: 'tournament',
    relatedTerms: ['bubble', 'pay_jump'],
    commonMistake: 'Playing ONLY to min-cash. Real tournament profit comes from final tables - min-cashing every event slowly loses money.',
    proTip: 'After the bubble bursts, most players relax - that\'s your window to accumulate cheap chips for a deep run.',
  },
  satellite: {
    term: 'Satellite',
    definition: 'A qualifier tournament whose prize is entry into a bigger event. All qualifying finishes win identical prizes.',
    simpleDefinition: 'Win a cheap tournament, get a ticket to an expensive one! Whether you finish 1st or 10th, the ticket is exactly the same.',
    example: 'A $10 satellite awards 10 seats to a $100 event. Finishing 1st and 10th both win one $100 seat - no difference at all.',
    level: 'intermediate',
    category: 'tournament',
    relatedTerms: ['icm', 'bubble'],
    commonMistake: 'Trying to win the satellite outright. First place gets nothing extra - once your seat is safe, folding EVERYTHING (even aces!) is often right.',
    proTip: 'Near the satellite bubble, count how many blinds shorter stacks can survive - if they must bust before you, fold your way to the seat.',
  },
  pko: {
    term: 'PKO (Progressive Knockout)',
    definition: 'A bounty tournament where part of each buy-in becomes a price on your head. Busting a player wins half their bounty in cash; the other half grows your own bounty.',
    simpleDefinition: 'Every player has a cash prize on their head. Knock someone out, collect it! Half goes in your pocket, half makes YOUR head worth more.',
    example: 'You bust a player with a $12 bounty: $6 cash to you instantly, $6 added to your own bounty - making you a bigger target.',
    level: 'intermediate',
    category: 'tournament',
    relatedTerms: ['bounty', 'coverage'],
    commonMistake: 'Playing PKOs like regular tournaments. Bounties are extra prize money in every pot against shorter stacks - ranges must widen.',
    proTip: 'Convert bounties to chips: a bounty worth a third of the starting stack is worth a third of a starting stack of extra "pot" when you can win it.',
  },
  bounty: {
    term: 'Bounty',
    definition: 'The cash reward for eliminating a specific player in a knockout tournament.',
    simpleDefinition: 'The prize money you collect for busting someone. Bigger stacks that keep knocking players out carry bigger bounties.',
    example: 'The chip leader has knocked out six players - their bounty has snowballed to $40. Busting them pays you $20 instantly.',
    level: 'beginner',
    category: 'tournament',
    relatedTerms: ['pko', 'coverage'],
    proTip: 'Late in a PKO, giant bounties justify calling wider than any chart says - the bounty is a freeroll stapled to the pot.',
  },
  coverage: {
    term: 'Coverage (Covering)',
    definition: 'Having more chips than an opponent. In bounty formats only the covering player can win the bounty; in any format only the covered player\'s tournament life is at risk.',
    simpleDefinition: 'You "cover" someone when you have more chips than them. They can bust this hand - you can\'t. In bounty games, only YOU can claim their bounty.',
    example: 'You have 20bb, they jam 14bb. You cover them: worst case you drop to 6bb, but they can be eliminated - and their bounty is claimable.',
    level: 'intermediate',
    category: 'tournament',
    relatedTerms: ['pko', 'bounty', 'effective_stack'],
    commonMistake: 'Calling wide for a bounty you CANNOT win because they cover you. No coverage, no bounty, no wide call.',
    proTip: 'Covering both blinds in a PKO is the green light for relentless raising - every jam into you risks their bounty.',
  },
  stop_and_go: {
    term: 'Stop & Go',
    definition: 'A short-stack play: call a raise out of position preflop, then jam any flop - moving your all-in to the street where the opponent has usually missed.',
    simpleDefinition: 'Instead of shoving before the flop (where they\'ll call), just call - then shove the flop no matter what comes. Most flops miss them, so they fold way more often!',
    example: 'At 9bb in the big blind vs a button raise with 88: call, then jam any flop. Unpaired hands miss two-thirds of flops and must fold.',
    level: 'advanced',
    category: 'tournament',
    relatedTerms: ['push_fold', 'fold_equity', 'jam'],
    commonMistake: 'Stop-and-go with hands like AK that WANT a preflop call. Use it with vulnerable made hands, not hands that dominate the caller.',
    proTip: 'The play only works first to act - the whole point is jamming before they can check-and-see.',
  },
  limp_jam: {
    term: 'Limp-Jam',
    definition: 'Limping a premium hand short-stacked, intending to go all-in over an expected raise.',
    simpleDefinition: 'Just call with a monster hand, wait for an aggressive player to raise, then spring the trap - all-in!',
    example: 'At 11bb in the small blind with aces vs an aggressive big blind: limp, they raise to attack your "weakness", you jam - they\'re already committed.',
    level: 'advanced',
    category: 'tournament',
    relatedTerms: ['limp', 'jam', 'slow_play'],
    commonMistake: 'Limp-jamming against passive players who check behind - the trap needs an aggressor to spring it.',
    proTip: 'Once you limp-jam aces, occasionally limp-jam weaker hands too - or observant players will never raise your limps again.',
  },
  chip_leader: {
    term: 'Chip Leader',
    definition: 'The player with the most chips at the table or in the tournament, able to pressure everyone without risking elimination.',
    simpleDefinition: 'The player with the biggest stack. They can threaten everyone\'s tournament life - nobody can threaten theirs.',
    example: 'As chip leader on the bubble, you raise every hand - the medium stacks can\'t afford to fight back.',
    level: 'beginner',
    category: 'tournament',
    relatedTerms: ['coverage', 'bubble', 'icm'],
    commonMistake: 'Bullying the WRONG stacks - micro-stacks must gamble and will call. Pressure the medium stacks with something to lose.',
    proTip: 'The other big stack is your only real threat - stay out of their way and farm everyone else.',
  },
};

// ============================================================================
// GLOSSARY HELPER FUNCTIONS
// ============================================================================

export function getGlossaryByCategory(category: GlossaryEntry['category']): GlossaryEntry[] {
  return Object.values(POKER_GLOSSARY).filter(entry => entry.category === category);
}

export function getGlossaryByLevel(level: GlossaryEntry['level']): GlossaryEntry[] {
  return Object.values(POKER_GLOSSARY).filter(entry => entry.level === level);
}

export function searchGlossary(query: string): GlossaryEntry[] {
  const normalized = query.toLowerCase().replace(/[-_]/g, '').trim();
  return Object.entries(POKER_GLOSSARY)
    .filter(([key, entry]) => 
      key.toLowerCase().replace(/_/g, '').includes(normalized) ||
      entry.term.toLowerCase().includes(normalized) ||
      entry.definition.toLowerCase().includes(normalized)
    )
    .map(([_, entry]) => entry);
}

export function getHandRankings(): GlossaryEntry[] {
  const order = ['royal_flush', 'straight_flush', 'four_of_a_kind', 'full_house', 'flush', 
                 'straight', 'three_of_a_kind', 'two_pair', 'one_pair', 'high_card'];
  return order.map(key => POKER_GLOSSARY[key]).filter(Boolean);
}

export function getRelatedTerms(termKey: string): GlossaryEntry[] {
  const entry = POKER_GLOSSARY[termKey];
  if (!entry?.relatedTerms) return [];
  return entry.relatedTerms.map(key => POKER_GLOSSARY[key]).filter(Boolean);
}

export const GLOSSARY_STATS = {
  totalTerms: Object.keys(POKER_GLOSSARY).length,
  byLevel: {
    'absolute-beginner': Object.values(POKER_GLOSSARY).filter(e => e.level === 'absolute-beginner').length,
    'beginner': Object.values(POKER_GLOSSARY).filter(e => e.level === 'beginner').length,
    'intermediate': Object.values(POKER_GLOSSARY).filter(e => e.level === 'intermediate').length,
    'advanced': Object.values(POKER_GLOSSARY).filter(e => e.level === 'advanced').length,
  },
  byCategory: {
    'basics': Object.values(POKER_GLOSSARY).filter(e => e.category === 'basics').length,
    'actions': Object.values(POKER_GLOSSARY).filter(e => e.category === 'actions').length,
    'structure': Object.values(POKER_GLOSSARY).filter(e => e.category === 'structure').length,
    'hand-rankings': Object.values(POKER_GLOSSARY).filter(e => e.category === 'hand-rankings').length,
    'positions': Object.values(POKER_GLOSSARY).filter(e => e.category === 'positions').length,
    'streets': Object.values(POKER_GLOSSARY).filter(e => e.category === 'streets').length,
    'strategy': Object.values(POKER_GLOSSARY).filter(e => e.category === 'strategy').length,
    'stats': Object.values(POKER_GLOSSARY).filter(e => e.category === 'stats').length,
    'player-types': Object.values(POKER_GLOSSARY).filter(e => e.category === 'player-types').length,
    'tournament': Object.values(POKER_GLOSSARY).filter(e => e.category === 'tournament').length,
  },
};

console.log(`📚 POKER GLOSSARY: ${GLOSSARY_STATS.totalTerms} terms loaded`);


// ============================================================================
// TYPES & INTERFACES (From Original)
// ============================================================================

export type DecisionQuality = 'excellent' | 'good' | 'neutral' | 'suboptimal' | 'poor';

export interface DetailedExample {
  scenario: string;
  yourHand: string;
  board: string;
  position: string;
  potSize: number;
  action: string;
  sizing?: number;
  whatYouDid: string;
  whatYouShouldDo: string;
  whyItMatters: string;
  conceptsToStudy: string[];
}

export interface DecisionEvaluation {
  decision: LoggedDecision;
  quality: DecisionQuality;
  score: number;
  metric: string;
  reason: string;
  advice?: string;
  detailedExample?: DetailedExample;
}

export interface MetricResult {
  name: string;
  displayName: string;
  score: number;
  weight: number;
  evaluations: DecisionEvaluation[];
  summary: string;
  strengths: string[];
  weaknesses: string[];
  drills: string[];
  keyExamples: DetailedExample[];
  conceptDefinitions: string[];
}

export interface PositionStats {
  position: PositionCategory;
  vpip: number;
  pfr: number;
  aggressionFactor: number;
  handsPlayed: number;
  totalHands: number;
}

export interface TrainingReport {
  reportId: string;
  sessionId: string;
  uid: string;
  username: string;
  
  generatedAt: number;
  sessionDuration: number;
  
  overallScore: number;
  skillLevel: 'Beginner' | 'Developing' | 'Intermediate' | 'Advanced' | 'Expert';
  handsPlayed: number;
  decisionsAnalyzed: number;
  
  stats: {
    vpip: number;
    pfr: number;
    aggressionFactor: number;
    cbetFrequency: number;
    bluffFrequency: number;
    wtsd: number;
    wonAtShowdown: number;
  };
  
  positionStats: PositionStats[];
  metrics: MetricResult[];
  
  topStrengths: string[];
  topWeaknesses: string[];
  keyMistakes: DecisionEvaluation[];
  bestPlays: DecisionEvaluation[];
  recommendedDrills: string[];
  
  glossaryTerms: Record<string, GlossaryEntry>;
  detailedExamples: DetailedExample[];
  nextStepsGuide: string[];

  // Hand-by-hand review rows: the exact hands from this session with the
  // player's action, the coach's verdict, and the better line when there
  // was one. Rendered by TrainingReportPage.
  keyHands?: Array<{
    handNumber: number;
    position: string;
    holeCards: string[];
    board: string[];
    potSize: number;
    street: string;
    userAction: string;
    userAmount: number;
    correctAction: string;
    wasCorrect: boolean;
    isMistake: boolean;
    isGoodPlay: boolean;
    explanation: string;
    conceptsTested: string[];
    // Deep decision data for the AI coach's hand-level analysis
    actionRaw?: string;
    betAmount?: number;
    equity?: number;
    potOdds?: number;
    requiredEquity?: number;
    facingBet?: boolean;
    facingRaise?: boolean;
    isFirstToAct?: boolean;
    playersInHand?: number;
    effectiveStack?: number;
    stackSize?: number;
    bigBlind?: number;
    handDescription?: string;
    // Full action line and hand result
    actions?: Array<{ street: string; action: string; amount: number; pot: number }>;
    won?: boolean;
    profit?: number;
    showdown?: boolean;
  }>;

  // Run verdict for challenge sessions - set when the challenge settled.
  challengeOutcome?: { outcome: 'won' | 'lost'; heroChips: number; handsPlayed: number };

  // The challenge this session was played under (challenge tables only) -
  // lets the report page and AI coach frame hands against the challenge's
  // winning approach.
  challenge?: {
    id: string;
    title: string;
    mission?: string;
    tips?: string[];
    skill?: string;
    concept?: string;
    goalType?: string;
    goalChips?: number;
    heroChips?: number;
    maxHands?: number;
  };

  previousScore?: number;
  scoreChange?: number;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const PREMIUM_HANDS = ['AA', 'KK', 'QQ', 'AKs', 'AKo'];
const STRONG_HANDS = ['JJ', 'TT', 'AQs', 'AQo', 'AJs', 'KQs'];
const MEDIUM_HANDS = ['99', '88', '77', 'ATs', 'A9s', 'KJs', 'KTs', 'QJs', 'QTs', 'JTs'];
const SPECULATIVE_HANDS = ['66', '55', '44', '33', '22', 'A8s', 'A7s', 'A6s', 'A5s', 'A4s', 'A3s', 'A2s', 
                          'K9s', 'Q9s', 'J9s', 'T9s', '98s', '87s', '76s', '65s', '54s'];

const POSITION_RANGES: Record<PositionCategory, string[]> = {
  'EP': [...PREMIUM_HANDS, ...STRONG_HANDS.slice(0, 4)],
  'MP': [...PREMIUM_HANDS, ...STRONG_HANDS, ...MEDIUM_HANDS.slice(0, 5)],
  'LP': [...PREMIUM_HANDS, ...STRONG_HANDS, ...MEDIUM_HANDS, ...SPECULATIVE_HANDS],
  'BLINDS': [...PREMIUM_HANDS, ...STRONG_HANDS, ...MEDIUM_HANDS],
};

function formatCards(cards: { rank: string; suit: string }[]): string {
  const suitSymbols: Record<string, string> = {
    hearts: '♥', diamonds: '♦', clubs: '♣', spades: '♠',

    h: '', d: '', c: '', s: '',

  };
  return cards.map(c => `${c.rank}${suitSymbols[c.suit] || c.suit}`).join(' ');
}

function getHandNotation(holeCards: { rank: string; suit: string }[]): string {
  if (holeCards.length !== 2) return '';
  
  const [card1, card2] = holeCards;
  const ranks = [card1.rank, card2.rank].sort((a, b) => {
    const order = '23456789TJQKA';
    return order.indexOf(b) - order.indexOf(a);
  });
  
  const suited = card1.suit === card2.suit;
  const paired = card1.rank === card2.rank;
  
  if (paired) return `${ranks[0]}${ranks[1]}`;
  return `${ranks[0]}${ranks[1]}${suited ? 's' : 'o'}`;
}

function isHandInRange(handNotation: string, position: PositionCategory): boolean {
  const range = POSITION_RANGES[position];
  if (range.includes(handNotation)) return true;
  const baseHand = handNotation.slice(0, 2);
  if (range.includes(baseHand + 's') || range.includes(baseHand + 'o')) return true;
  if (range.includes(baseHand)) return true;
  return false;
}

function getQualityScore(quality: DecisionQuality): number {
  switch (quality) {
    case 'excellent': return 1.0;
    case 'good': return 0.85;
    case 'neutral': return 0.7;
    case 'suboptimal': return 0.5;
    case 'poor': return 0.2;
  }
}

function getSkillLevel(score: number): 'Beginner' | 'Developing' | 'Intermediate' | 'Advanced' | 'Expert' {
  if (score >= 8.5) return 'Expert';
  if (score >= 7.0) return 'Advanced';
  if (score >= 5.5) return 'Intermediate';
  if (score >= 4.0) return 'Developing';
  return 'Beginner';
}

function formatMoney(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

function formatPercent(value: number): string {
  return `${value.toFixed(0)}%`;
}

function createDetailedExample(
  decision: LoggedDecision,
  whatYouShouldDo: string,
  whyItMatters: string,
  conceptsToStudy: string[]
): DetailedExample {
  return {
    scenario: `Hand #${decision.handNumber} - ${decision.street.charAt(0).toUpperCase() + decision.street.slice(1)}`,
    yourHand: formatCards(decision.holeCards),
    board: decision.communityCards.length > 0 ? formatCards(decision.communityCards) : 'Preflop',
    position: decision.position,
    potSize: decision.potSizeBeforeAction,
    action: decision.action,
    sizing: decision.betAmount > 0 ? decision.betAmount : undefined,
    whatYouDid: `You ${decision.action}${decision.betAmount > 0 ? `ed ${formatMoney(decision.betAmount)}` : ''}`,
    whatYouShouldDo,
    whyItMatters,
    conceptsToStudy,
  };
}
function evaluatePreflopDiscipline(decisions: LoggedDecision[]): MetricResult {
  const preflopDecisions = decisions.filter(d => d.street === 'preflop');
  const evaluations: DecisionEvaluation[] = [];
  const strengths: string[] = [];
  const weaknesses: string[] = [];
  const keyExamples: DetailedExample[] = [];
  
  for (const decision of preflopDecisions) {
    const handNotation = getHandNotation(decision.holeCards);
    const inRange = isHandInRange(handNotation, decision.positionCategory);
    const isVPIP = decision.action !== 'fold';
    const isRaise = decision.action === 'raise' || decision.action === 'bet';
    
    let quality: DecisionQuality = 'good';
    let reason = '';
    let advice: string | undefined;
    let detailedExample: DetailedExample | undefined;
    
    if (inRange && isVPIP) {
      quality = isRaise ? 'excellent' : 'good';
      reason = `Correctly played ${handNotation} from ${decision.position}`;
      
      if (quality === 'excellent') {
        detailedExample = createDetailedExample(
          decision,
          `Raise with ${handNotation} from ${decision.position} - exactly right!`,
          'Raising with strong hands builds the pot and denies equity to weaker hands.',
          ['position', 'pfr', 'range']
        );
      }
    } else if (!inRange && !isVPIP) {
      quality = 'good';
      reason = `Correctly folded ${handNotation} from ${decision.position}`;
    } else if (!inRange && isVPIP) {
      const optimalRange = POSITION_RANGES[decision.positionCategory].slice(0, 5).join(', ');
      
      if (decision.positionCategory === 'EP') {
        quality = 'poor';
        reason = `Played ${handNotation} from early position - too loose`;
        advice = `In early position (UTG/UTG+1), stick to premium hands: ${optimalRange}`;
        
        detailedExample = createDetailedExample(
          decision,
          `FOLD ${handNotation} from ${decision.position}. Too many players left to act.`,
          `From early position, 5-6 players still have to act. Playing weak hands here means you'll often face raises and play out of position. ${handNotation} is not strong enough to overcome these disadvantages.`,
          ['position', 'vpip', 'range']
        );
        keyExamples.push(detailedExample);
        
      } else if (decision.positionCategory === 'MP') {
        quality = 'suboptimal';
        reason = `Played ${handNotation} from middle position - slightly loose`;
        advice = 'Middle position allows more hands, but this one is marginal';
        
      } else if (decision.positionCategory === 'BLINDS') {
        quality = 'neutral';
        reason = `Played ${handNotation} from the ${decision.position} - blind battles are the toughest spots, keep the bar high`;
      } else {
        quality = 'neutral';
        reason = `Played ${handNotation} from late position - acceptable but risky`;
      }
    } else if (inRange && !isVPIP) {
      if (PREMIUM_HANDS.includes(handNotation)) {
        quality = 'poor';
        reason = `Folded premium hand ${handNotation} from ${decision.position}!`;
        advice = 'NEVER fold premium hands preflop (AA, KK, QQ, AK)';
        
        detailedExample = createDetailedExample(
          decision,
          `RAISE ${handNotation}! This is a top 5 starting hand.`,
          `${handNotation} is one of the strongest starting hands in poker. You should raise for value 100% of the time. Folding this hand throws away significant expected value.`,
          ['vpip', 'pfr', 'range']
        );
        keyExamples.push(detailedExample);
        
      } else {
        quality = 'suboptimal';
        reason = `Folded playable hand ${handNotation} from ${decision.position}`;
        advice = `${handNotation} is profitable to play from ${decision.position}`;
      }
    }
    
    evaluations.push({
      decision,
      quality,
      score: getQualityScore(quality),
      metric: 'preflop_discipline',
      reason,
      advice,
      detailedExample,
    });
  }
  
  const avgScore = evaluations.length > 0
    ? evaluations.reduce((sum, e) => sum + e.score, 0) / evaluations.length
    : 0.5;
  
  const poorFromEP = evaluations.filter(e => 
    e.quality === 'poor' && e.decision.positionCategory === 'EP'
  ).length;
  
  const foldedPremiums = evaluations.filter(e =>
    e.quality === 'poor' && e.reason.includes('Folded premium')
  ).length;
  
  if (poorFromEP === 0 && avgScore >= 0.8) {
    strengths.push('Solid starting hand selection');
    strengths.push('Good position-aware hand selection');
  }
  
  if (poorFromEP >= 2) {
    weaknesses.push(`Playing ${poorFromEP} too many hands from early position`);
  }
  
  if (foldedPremiums > 0) {
    weaknesses.push(`Folded ${foldedPremiums} premium hand(s) - major leak!`);
  }
  
  return {
    name: 'preflop_discipline',
    displayName: 'Preflop Discipline',
    score: avgScore * 10,
    weight: 0.25,
    evaluations,
    summary: avgScore >= 0.8 
      ? 'Your preflop hand selection is solid - you understand position and ranges'
      : avgScore >= 0.6
        ? 'Your preflop game needs tightening - focus on position-based ranges'
        : 'Significant leaks in preflop selection - study the fundamentals',
    strengths,
    weaknesses,
    drills: weaknesses.length > 0 ? [
      ' Challenge: Play only top 10 hands from UTG for 20 hands',

      ' Memorize the position-based opening ranges below',

      ' Fold any hand not on your range chart - no exceptions',

    ] : ['Keep up the solid preflop play!'],
    keyExamples,
    conceptDefinitions: ['vpip', 'pfr', 'position', 'range'],
  };
}

function evaluatePositionAwareness(decisions: LoggedDecision[]): MetricResult {
  const evaluations: DecisionEvaluation[] = [];
  const strengths: string[] = [];
  const weaknesses: string[] = [];
  const keyExamples: DetailedExample[] = [];
  
  const positionStats: Record<PositionCategory, { vpip: number; pfr: number; total: number; bets: number; calls: number }> = {
    'EP': { vpip: 0, pfr: 0, total: 0, bets: 0, calls: 0 },
    'MP': { vpip: 0, pfr: 0, total: 0, bets: 0, calls: 0 },
    'LP': { vpip: 0, pfr: 0, total: 0, bets: 0, calls: 0 },
    'BLINDS': { vpip: 0, pfr: 0, total: 0, bets: 0, calls: 0 },
  };
  
  const preflopDecisions = decisions.filter(d => d.street === 'preflop');
  for (const decision of preflopDecisions) {
    const pos = decision.positionCategory;
    positionStats[pos].total++;
    
    if (decision.action !== 'fold') {
      positionStats[pos].vpip++;
      if (decision.action === 'raise' || decision.action === 'bet') {
        positionStats[pos].pfr++;
        positionStats[pos].bets++;
      } else {
        positionStats[pos].calls++;
      }
    }
  }
  
  const postflopDecisions = decisions.filter(d => d.street !== 'preflop');
  for (const decision of postflopDecisions) {
    const pos = decision.positionCategory;
    if (decision.action === 'bet' || decision.action === 'raise') {
      positionStats[pos].bets++;
    } else if (decision.action === 'call') {
      positionStats[pos].calls++;
    }
  }
  
  const guidelines = {
    'EP': { minVPIP: 10, maxVPIP: 15, name: 'Early Position (UTG, UTG+1)' },
    'MP': { minVPIP: 15, maxVPIP: 22, name: 'Middle Position (MP, MP+1)' },
    'LP': { minVPIP: 25, maxVPIP: 40, name: 'Late Position (CO, BTN)' },
    'BLINDS': { minVPIP: 12, maxVPIP: 25, name: 'Blinds (SB, BB)' },
  };
  
  let totalScore = 0;
  let positionsEvaluated = 0;
  
  for (const [pos, stats] of Object.entries(positionStats)) {
    if (stats.total < 2) continue;
    
    const vpipPercent = (stats.vpip / stats.total) * 100;
    const aggFactor = stats.calls > 0 ? stats.bets / stats.calls : stats.bets;
    const guide = guidelines[pos as PositionCategory];
    
    let quality: DecisionQuality = 'good';
    let reason = '';
    let detailedExample: DetailedExample | undefined;
    
    if (vpipPercent > guide.maxVPIP + 15) {
      quality = 'poor';
      reason = `VPIP too high in ${pos}: ${vpipPercent.toFixed(0)}% (target: ${guide.minVPIP}-${guide.maxVPIP}%)`;
      weaknesses.push(`Playing ${(vpipPercent - guide.maxVPIP).toFixed(0)}% too many hands from ${guide.name}`);
      
    } else if (vpipPercent > guide.maxVPIP + 5) {
      quality = 'suboptimal';
      reason = `VPIP slightly high in ${pos}: ${vpipPercent.toFixed(0)}%`;
      
    } else if (vpipPercent < guide.minVPIP - 5 && pos === 'LP') {
      quality = 'suboptimal';
      reason = `VPIP too tight in late position: ${vpipPercent.toFixed(0)}% (should be ${guide.minVPIP}%+)`;
      weaknesses.push('Missing profitable steal opportunities from Button/Cutoff');
      
      // Find a folded hand example from LP
      const foldedLP = preflopDecisions.find(d => 
        d.positionCategory === 'LP' && d.action === 'fold' && 
        (d.holeCards.some(c => c.rank === 'A') || d.holeCards.some(c => c.rank === 'K'))
      );
      
      if (foldedLP) {
        detailedExample = createDetailedExample(
          foldedLP,
          `RAISE ${formatCards(foldedLP.holeCards)} from the ${foldedLP.position}!`,
          `On the Button/Cutoff, you can profitably open 25-35% of hands. You act LAST postflop, which is a huge advantage. Hands like ${getHandNotation(foldedLP.holeCards)} are easy opens.`,
          ['position', 'vpip']
        );
        keyExamples.push(detailedExample);
      }
      
    } else {
      quality = 'good';
      reason = `Good VPIP range in ${pos}: ${vpipPercent.toFixed(0)}%`;
      if (pos === 'EP' && vpipPercent <= guide.maxVPIP) {
        strengths.push('Disciplined play from early position');
      }
      if (pos === 'LP' && vpipPercent >= guide.minVPIP) {
        strengths.push('Utilizing late position advantage');
      }
    }
    
    if (pos === 'LP' && aggFactor < 1.5 && stats.bets + stats.calls >= 5) {
      weaknesses.push(`Low aggression from late position (AF: ${aggFactor.toFixed(1)}, target: 2.0+)`);
    }
    
    totalScore += getQualityScore(quality);
    positionsEvaluated++;
    
    evaluations.push({
      decision: preflopDecisions.find(d => d.positionCategory === pos) || preflopDecisions[0],
      quality,
      score: getQualityScore(quality),
      metric: 'position_awareness',
      reason,
      detailedExample,
    });
  }
  
  const avgScore = positionsEvaluated > 0 ? totalScore / positionsEvaluated : 0.5;
  
  return {
    name: 'position_awareness',
    displayName: 'Position Awareness',
    score: avgScore * 10,
    weight: 0.20,
    evaluations,
    summary: avgScore >= 0.8
      ? 'Excellent position awareness - you play tight early and loose late'
      : avgScore >= 0.6
        ? 'Decent positional play but room to optimize'
        : 'Position awareness needs significant work',
    strengths: strengths.length > 0 ? strengths : ['Working on position fundamentals'],
    weaknesses,
    drills: [
      ' EP Challenge: Only open AA-TT, AK, AQ from UTG/UTG+1',

      ' BTN Challenge: Open 30%+ of hands when folded to you on the Button',

      ' Track your VPIP by position for 50 hands',

    ],
    keyExamples,
    conceptDefinitions: ['position', 'vpip', 'aggFactor'],
  };
}

function evaluatePostflopAggression(decisions: LoggedDecision[]): MetricResult {
  const evaluations: DecisionEvaluation[] = [];
  const strengths: string[] = [];
  const weaknesses: string[] = [];
  const keyExamples: DetailedExample[] = [];
  
  const flopDecisions = decisions.filter(d => 
    d.street === 'flop' && d.wasPreflopRaiser
  );
  
  let cbetOpportunities = 0;
  let cbetsMade = 0;
  
  for (const decision of flopDecisions) {
    cbetOpportunities++;
    const isCbet = decision.action === 'bet' || decision.action === 'raise';
    if (isCbet) cbetsMade++;
    
    let quality: DecisionQuality = 'good';
    let reason = '';
    let advice: string | undefined;
    let detailedExample: DetailedExample | undefined;
    
    // Evaluate C-bet decision based on board texture
    if (decision.boardTexture === 'dry' && !isCbet && decision.handStrength !== 'nothing') {
      quality = 'suboptimal';
      reason = 'Missed C-bet on dry board';
      advice = 'C-bet 60-80% on dry boards (like K-7-2) to apply pressure';
      
      const optimalBet = Math.round(decision.potSizeBeforeAction * 0.5);
      detailedExample = createDetailedExample(
        decision,
        `BET ${formatMoney(optimalBet)} (50% pot) as a continuation bet`,
        `You raised preflop, so you should usually continue betting on the flop. This board (${formatCards(decision.communityCards)}) is DRY - few draws possible. Your C-bet tells a consistent story and wins the pot often.`,
        ['cbet', 'boardTexture']
      );
      keyExamples.push(detailedExample);
      
    } else if (decision.boardTexture === 'wet' && isCbet && 
               decision.handStrength === 'nothing' && decision.drawType === 'none') {
      quality = 'suboptimal';
      reason = 'Risky C-bet on wet board with no equity';
      advice = 'Check more often on wet boards when you miss completely';
      
      detailedExample = createDetailedExample(
        decision,
        `CHECK and give up on wet boards when you have nothing`,
        `Board: ${formatCards(decision.communityCards)} is WET - many draws and made hands possible. Betting with ${formatCards(decision.holeCards)} (no equity) is lighting money on fire. Check and fold to aggression.`,
        ['cbet', 'boardTexture', 'equity']
      );
      keyExamples.push(detailedExample);
      
    } else if (isCbet && decision.handStrength === 'strong') {
      quality = 'excellent';
      reason = 'Strong value C-bet';
      
    } else if (isCbet) {
      // Check sizing
      const sizingRatio = decision.betSizeRelativeToPot;
      if (sizingRatio < 0.3) {
        quality = 'suboptimal';
        reason = `C-bet too small (${formatPercent(sizingRatio * 100)} pot) - not applying enough pressure`;
        advice = 'Size C-bets at 50-75% pot to maximize fold equity';
        
        const optimalBet = Math.round(decision.potSizeBeforeAction * 0.6);
        detailedExample = createDetailedExample(
          decision,
          `BET ${formatMoney(optimalBet)} (60% pot) instead of ${formatMoney(decision.betAmount)}`,
          `Your bet of ${formatMoney(decision.betAmount)} into ${formatMoney(decision.potSizeBeforeAction)} is only ${formatPercent(sizingRatio * 100)} pot. This is too small - you give drawing hands correct odds to call. Bet 50-75% pot to charge draws.`,
          ['cbet', 'potOdds']
        );
        keyExamples.push(detailedExample);
        
      } else if (sizingRatio > 1.2) {
        quality = 'neutral';
        reason = `C-bet sizing large (${formatPercent(sizingRatio * 100)} pot) - may be overbluffing`;
        advice = 'Standard C-bet sizing is 50-75% pot';
      } else {
        quality = 'good';
        reason = 'Well-sized continuation bet';
      }
    }
    
    evaluations.push({
      decision,
      quality,
      score: getQualityScore(quality),
      metric: 'postflop_aggression',
      reason,
      advice,
      detailedExample,
    });
  }
  
  // Evaluate value betting on later streets
  const valueBettingDecisions = decisions.filter(d => 
    (d.street === 'turn' || d.street === 'river') && 
    (d.handStrength === 'strong' || d.handStrength === 'monster')
  );
  
  for (const decision of valueBettingDecisions) {
    const bet = decision.action === 'bet' || decision.action === 'raise';
    
    if (!bet) {
      const optimalBet = Math.round(decision.potSizeBeforeAction * 0.7);
      
      evaluations.push({
        decision,
        quality: 'suboptimal',
        score: getQualityScore('suboptimal'),
        metric: 'postflop_aggression',
        reason: `Missed value bet on ${decision.street} with ${decision.handDescription}`,
        advice: `Bet ${formatMoney(optimalBet)} for value - weaker hands will call`,
        detailedExample: createDetailedExample(
          decision,
          `BET ${formatMoney(optimalBet)} (70% pot) for VALUE`,
          `You have ${decision.handDescription} - a strong hand! Checking gives up value. Many worse hands (top pair, second pair, draws) will call a bet. Extract maximum value from your big hands.`,
          ['aggFactor']
        ),
      });
      weaknesses.push('Missing value bets with strong hands');
      keyExamples.push(evaluations[evaluations.length - 1].detailedExample!);
      
    } else {
      evaluations.push({
        decision,
        quality: 'excellent',
        score: getQualityScore('excellent'),
        metric: 'postflop_aggression',
        reason: 'Good value bet with strong hand',
      });
      strengths.push('Extracting value with strong hands');
    }
  }
  
  const cbetFreq = cbetOpportunities > 0 ? (cbetsMade / cbetOpportunities) * 100 : 0;
  
  if (cbetFreq >= 50 && cbetFreq <= 75) {
    strengths.push(`Healthy C-bet frequency: ${formatPercent(cbetFreq)}`);
  } else if (cbetFreq < 40 && cbetOpportunities >= 3) {
    weaknesses.push(`C-bet frequency too low: ${formatPercent(cbetFreq)} (target: 50-70%)`);
  } else if (cbetFreq > 85 && cbetOpportunities >= 3) {
    weaknesses.push(`C-betting too much: ${formatPercent(cbetFreq)} - opponents will adjust`);
  }
  
  const avgScore = evaluations.length > 0
    ? evaluations.reduce((sum, e) => sum + e.score, 0) / evaluations.length
    : 0.5;
  
  return {
    name: 'postflop_aggression',
    displayName: 'Post-flop Aggression',
    score: avgScore * 10,
    weight: 0.20,
    evaluations,
    summary: avgScore >= 0.8
      ? 'Strong postflop aggression - you control the pot effectively'
      : avgScore >= 0.6
        ? 'Decent postflop play with some missed opportunities'
        : 'Postflop aggression needs significant improvement',
    strengths: [...new Set(strengths)].slice(0, 3),
    weaknesses: [...new Set(weaknesses)].slice(0, 3),
    drills: [
      ' C-Bet Challenge: Bet 60% pot on every dry flop as the raiser',

      ' Value Town: Always bet your strong hands on river',

      ' Size It Right: Practice betting 50-75% pot consistently',

    ],
    keyExamples: keyExamples.slice(0, 3),
    conceptDefinitions: ['cbet', 'boardTexture', 'aggFactor'],
  };
}

function evaluateBluffingStrategy(decisions: LoggedDecision[]): MetricResult {
  const evaluations: DecisionEvaluation[] = [];
  const strengths: string[] = [];
  const weaknesses: string[] = [];
  const keyExamples: DetailedExample[] = [];
  
  const postflopDecisions = decisions.filter(d => d.street !== 'preflop');
  
  let bluffs = 0;
  let bluffOpportunities = 0;
  
  for (const decision of postflopDecisions) {
    const isBluff = (decision.action === 'bet' || decision.action === 'raise') &&
                    (decision.handStrength === 'weak' || decision.handStrength === 'nothing');
    
    const isSemiBluff = (decision.action === 'bet' || decision.action === 'raise') &&
                        decision.drawType !== 'none';
    
    const isBluffOpportunity = decision.handStrength === 'weak' || 
                               decision.handStrength === 'nothing' ||
                               decision.drawType !== 'none';
    
    if (isBluffOpportunity) {
      bluffOpportunities++;
      if (isBluff || isSemiBluff) bluffs++;
    }
    
    if (isBluff || isSemiBluff) {
      let quality: DecisionQuality = 'good';
      let reason = '';
      let advice: string | undefined;
      let detailedExample: DetailedExample | undefined;
      
      // Semi-bluff with strong draw
      if (isSemiBluff && (decision.drawType === 'flush-draw' || decision.drawType === 'oesd' || decision.drawType === 'combo-draw')) {
        quality = 'excellent';
        reason = `Great semi-bluff with ${decision.drawType.replace('-', ' ')}`;
        
        detailedExample = createDetailedExample(
          decision,
          `Perfect semi-bluff! You can win by them folding OR by hitting your draw.`,
          `With ${formatCards(decision.holeCards)} on ${formatCards(decision.communityCards)}, you have ${decision.outs} outs (~${(decision.equity).toFixed(0)}% equity). Semi-bluffing is optimal because you have two ways to win.`,
          ['semiBluff', 'equity', 'outs']
        );
        strengths.push('Effective semi-bluffing with draws');
        
      }
      // Bluff from late position on dry board - good
      else if (decision.positionCategory === 'LP' && decision.boardTexture === 'dry' && decision.opponentCount <= 2) {
        quality = 'good';
        reason = 'Well-timed bluff from position on dry board';
        
      }
      // Pure bluff from early position - bad
      else if (decision.positionCategory === 'EP' && !isSemiBluff) {
        quality = 'poor';
        reason = 'Bluffing from early position - too many players behind';
        advice = 'Only bluff from late position where you have information advantage';
        
        detailedExample = createDetailedExample(
          decision,
          `CHECK or FOLD - never bluff from early position`,
          `From ${decision.position}, you have ${decision.opponentCount} players behind you. Any of them could have a strong hand. Bluffing works best when: (1) you act LAST, (2) the board favors YOUR range, (3) there are few opponents.`,
          ['position', 'semiBluff']
        );
        keyExamples.push(detailedExample);
        weaknesses.push('Bluffing from early position');
        
      }
      // Bluff on wet board multiway - bad
      else if (decision.boardTexture === 'wet' && decision.opponentCount >= 2 && !isSemiBluff) {
        quality = 'suboptimal';
        reason = 'Bluffing on wet board against multiple opponents';
        advice = 'Bluffs work best heads-up on dry boards';
        
        detailedExample = createDetailedExample(
          decision,
          `CHECK - wet boards favor callers, not bluffers`,
          `Board ${formatCards(decision.communityCards)} is WET with many draws. Against ${decision.opponentCount} opponents, someone likely has a piece. Save bluffs for: (1) dry boards, (2) heads-up pots, (3) when you're in position.`,
          ['boardTexture', 'semiBluff']
        );
        keyExamples.push(detailedExample);
        
      }
      // Over-sized bluff
      else if (decision.betSizeRelativeToPot > 1.2) {
        quality = 'suboptimal';
        reason = `Bluff too large: ${formatPercent(decision.betSizeRelativeToPot * 100)} pot`;
        advice = 'Use smaller bluff sizes (50-75% pot) to risk less';
        
        const optimalBluff = Math.round(decision.potSizeBeforeAction * 0.6);
        detailedExample = createDetailedExample(
          decision,
          `Size bluffs at ${formatMoney(optimalBluff)} (60% pot), not ${formatMoney(decision.betAmount)}`,
          `You bet ${formatMoney(decision.betAmount)} into ${formatMoney(decision.potSizeBeforeAction)} (${formatPercent(decision.betSizeRelativeToPot * 100)} pot). This risks too much on a bluff. Smaller sizing achieves the same fold equity at lower cost.`,
          ['semiBluff', 'potOdds']
        );
        keyExamples.push(detailedExample);
        
      } else {
        quality = 'neutral';
        reason = 'Standard bluff attempt';
      }
      
      evaluations.push({
        decision,
        quality,
        score: getQualityScore(quality),
        metric: 'bluffing',
        reason,
        advice,
        detailedExample,
      });
    }
  }
  
  const bluffFreq = bluffOpportunities > 0 ? (bluffs / bluffOpportunities) * 100 : 0;
  
  if (bluffFreq >= 15 && bluffFreq <= 35) {
    strengths.push(`Balanced bluff frequency: ${formatPercent(bluffFreq)}`);
  } else if (bluffFreq < 10 && bluffOpportunities >= 5) {
    weaknesses.push('Bluffing too rarely - becoming predictable');
  } else if (bluffFreq > 45) {
    weaknesses.push(`Bluffing too often: ${formatPercent(bluffFreq)} - easily exploitable`);
  }
  
  const avgScore = evaluations.length > 0
    ? evaluations.reduce((sum, e) => sum + e.score, 0) / evaluations.length
    : 0.7;
  
  return {
    name: 'bluffing',
    displayName: 'Bluffing Strategy',
    score: avgScore * 10,
    weight: 0.15,
    evaluations,
    summary: avgScore >= 0.8
      ? 'Smart bluffing - well-timed and properly sized'
      : avgScore >= 0.6
        ? 'Decent bluffing with room to optimize'
        : 'Bluffing strategy needs work',
    strengths: [...new Set(strengths)],
    weaknesses: [...new Set(weaknesses)],
    drills: [
      ' Bluff only from Button or Cutoff this session',

      ' Semi-Bluff: Bet all flush draws and straight draws',

      ' Size bluffs at 50-60% pot - never over 100%',

    ],
    keyExamples: keyExamples.slice(0, 3),
    conceptDefinitions: ['semiBluff', 'boardTexture', 'position'],
  };
}

function evaluatePotOddsDecisions(decisions: LoggedDecision[]): MetricResult {
  const evaluations: DecisionEvaluation[] = [];
  const strengths: string[] = [];
  const weaknesses: string[] = [];
  const keyExamples: DetailedExample[] = [];
  
  // Find drawing decisions
  const drawingDecisions = decisions.filter(d => 
    d.street !== 'preflop' && 
    d.facingBet &&
    d.drawType !== 'none'
  );
  
  for (const decision of drawingDecisions) {
    const potOdds = decision.potOdds; // e.g., 3 means 3:1
    const requiredEquity = decision.requiredEquity; // e.g., 25 for 25%
    const actualEquity = decision.equity;
    
    const hasOdds = actualEquity >= requiredEquity;
    const called = decision.action === 'call';
    const folded = decision.action === 'fold';
    
    let quality: DecisionQuality = 'good';
    let reason = '';
    let advice: string | undefined;
    let detailedExample: DetailedExample | undefined;
    
    if (hasOdds && called) {
      quality = 'excellent';
      reason = `Correct call with ${decision.drawType.replace('-', ' ')} - had ${actualEquity.toFixed(0)}% equity vs ${requiredEquity.toFixed(0)}% needed`;
      strengths.push('Correct pot odds calls');
      
    } else if (hasOdds && folded) {
      quality = 'suboptimal';
      reason = `Folded ${decision.drawType.replace('-', ' ')} with correct odds`;
      advice = `You had ${actualEquity.toFixed(0)}% equity but only needed ${requiredEquity.toFixed(0)}% - should call`;
      
      detailedExample = createDetailedExample(
        decision,
        `CALL - you have correct pot odds!`,
        `You had ${formatCards(decision.holeCards)} on ${formatCards(decision.communityCards)} = ${decision.outs} outs (${actualEquity.toFixed(0)}% equity).

POT ODDS CALCULATION:
• Pot was ${formatMoney(decision.potSizeBeforeAction)}
• You faced a bet requiring ${formatMoney(decision.betAmount)} to call
• Pot odds = ${decision.potOdds.toFixed(1)}:1 = need ${requiredEquity.toFixed(0)}% equity
• You have ${actualEquity.toFixed(0)}% > ${requiredEquity.toFixed(0)}% = PROFITABLE CALL

RULE OF 4 AND 2:
• Multiply outs × 4 on flop (2 cards to come) = equity %
• Multiply outs × 2 on turn (1 card to come) = equity %`,
        ['potOdds', 'equity', 'outs']
      );
      keyExamples.push(detailedExample);
      weaknesses.push('Folding draws with correct pot odds');
      
    } else if (!hasOdds && called) {
      quality = 'suboptimal';
      reason = `Called without proper pot odds - had ${actualEquity.toFixed(0)}% vs ${requiredEquity.toFixed(0)}% needed`;
      advice = `Need ${requiredEquity.toFixed(0)}% equity to call, only had ${actualEquity.toFixed(0)}%`;
      
      detailedExample = createDetailedExample(
        decision,
        `FOLD - you don't have pot odds!`,
        `POT ODDS MATH:
• Pot: ${formatMoney(decision.potSizeBeforeAction)} | Call: ${formatMoney(decision.betAmount)}
• Pot odds: ${decision.potOdds.toFixed(1)}:1 = need ${requiredEquity.toFixed(0)}% equity to call
• Your draw: ${decision.outs} outs = ${actualEquity.toFixed(0)}% equity
• ${actualEquity.toFixed(0)}% < ${requiredEquity.toFixed(0)}% = FOLD

Exception: Consider IMPLIED ODDS if opponent has big stack and will pay you off when you hit.`,
        ['potOdds', 'equity', 'impliedOdds']
      );
      keyExamples.push(detailedExample);
      weaknesses.push('Calling draws without pot odds');
      
    } else if (!hasOdds && folded) {
      quality = 'good';
      reason = 'Correct fold without pot odds';
    }
    
    evaluations.push({
      decision,
      quality,
      score: getQualityScore(quality),
      metric: 'pot_odds',
      reason,
      advice,
      detailedExample,
    });
  }
  
  // Evaluate semi-bluffs
  const semiBluffs = decisions.filter(d =>
    d.street !== 'preflop' &&
    (d.action === 'raise' || d.action === 'bet') &&
    d.drawType !== 'none' &&
    d.handStrength !== 'strong' &&
    d.handStrength !== 'monster'
  );
  
  for (const decision of semiBluffs) {
    let quality: DecisionQuality = 'good';
    let reason = '';
    
    if (decision.drawType === 'combo-draw' || decision.drawType === 'flush-draw' || decision.drawType === 'oesd') {
      quality = 'good';
      reason = `Good semi-bluff with ${decision.drawType.replace('-', ' ')} (${decision.outs} outs)`;
      strengths.push('Using semi-bluffs with draws');
    } else if (decision.drawType === 'gutshot') {
      quality = 'neutral';
      reason = 'Semi-bluff with weak draw - higher variance play';
    }
    
    evaluations.push({
      decision,
      quality,
      score: getQualityScore(quality),
      metric: 'pot_odds',
      reason,
    });
  }
  
  const avgScore = evaluations.length > 0
    ? evaluations.reduce((sum, e) => sum + e.score, 0) / evaluations.length
    : 0.7;
  
  return {
    name: 'pot_odds',
    displayName: 'Pot Odds & Math',
    score: avgScore * 10,
    weight: 0.20,
    evaluations,
    summary: avgScore >= 0.8
      ? 'Excellent pot odds awareness - you understand the math'
      : avgScore >= 0.6
        ? 'Decent understanding with some calculation errors'
        : 'Pot odds fundamentals need serious work',
    strengths: [...new Set(strengths)],
    weaknesses: [...new Set(weaknesses)],
    drills: [
      ' Memorize: Flush draw = 9 outs = 35% (flop) / 19% (turn)',

      ' Memorize: OESD = 8 outs = 31% (flop) / 17% (turn)',

      ' Memorize: Gutshot = 4 outs = 17% (flop) / 8% (turn)',

      ' Practice: Required equity = Call ÷ (Pot + Call) × 100',

    ],
    keyExamples: keyExamples.slice(0, 3),
    conceptDefinitions: ['potOdds', 'equity', 'outs', 'impliedOdds'],
  };
}

// ============================================================================
// MAIN ANALYSIS FUNCTION
// ============================================================================

export function analyzeTrainingSession(session: TrainingSession): TrainingReport {
  console.log(`📊 [TrainingAnalysis] Analyzing session ${session.sessionId}`);
  console.log(`   Hands: ${session.handsPlayed}, Decisions: ${session.decisions.length}`);
  
  // Run all metric evaluations
  const metrics: MetricResult[] = [
    evaluatePreflopDiscipline(session.decisions),
    evaluatePositionAwareness(session.decisions),
    evaluatePostflopAggression(session.decisions),
    evaluateBluffingStrategy(session.decisions),
    evaluatePotOddsDecisions(session.decisions),
  ];
  
  // Calculate overall score
  const overallScore = metrics.reduce((sum, m) => sum + (m.score * m.weight), 0);
  const skillLevel = getSkillLevel(overallScore);
  
  // Calculate basic stats
  const preflopDecisions = session.decisions.filter(d => d.street === 'preflop');
  const vpip = preflopDecisions.length > 0
    ? preflopDecisions.filter(d => d.action !== 'fold').length / preflopDecisions.length * 100
    : 0;
  const pfr = preflopDecisions.length > 0
    ? preflopDecisions.filter(d => d.action === 'raise' || d.action === 'bet').length / preflopDecisions.length * 100
    : 0;
  
  const postflopDecisions = session.decisions.filter(d => d.street !== 'preflop');
  const bets = postflopDecisions.filter(d => d.action === 'bet' || d.action === 'raise').length;
  const calls = postflopDecisions.filter(d => d.action === 'call').length;
  const aggressionFactor = calls > 0 ? bets / calls : bets;
  
  const cbetOpps = session.decisions.filter(d => d.street === 'flop' && d.wasPreflopRaiser).length;
  const cbetsMade = session.decisions.filter(d => 
    d.street === 'flop' && d.wasPreflopRaiser && (d.action === 'bet' || d.action === 'raise')
  ).length;
  const cbetFrequency = cbetOpps > 0 ? cbetsMade / cbetOpps * 100 : 0;
  
  const bluffs = session.decisions.filter(d =>
    d.street !== 'preflop' &&
    (d.action === 'bet' || d.action === 'raise') &&
    (d.handStrength === 'weak' || d.handStrength === 'nothing')
  ).length;
  const bluffOpps = session.decisions.filter(d =>
    d.street !== 'preflop' &&
    (d.handStrength === 'weak' || d.handStrength === 'nothing')
  ).length;
  const bluffFrequency = bluffOpps > 0 ? bluffs / bluffOpps * 100 : 0;
  
  const showdownHands = [...new Set(session.decisions.filter(d => d.showdownReached).map(d => d.handNumber))];
  const wonAtShowdown = [...new Set(session.decisions.filter(d => d.showdownReached && d.handWon).map(d => d.handNumber))];
  const wtsd = session.handsPlayed > 0 ? showdownHands.length / session.handsPlayed * 100 : 0;
  const wonAtShowdownPct = showdownHands.length > 0 ? wonAtShowdown.length / showdownHands.length * 100 : 0;
  
  // Position stats
  const positionStats: PositionStats[] = [];
  for (const pos of ['EP', 'MP', 'LP', 'BLINDS'] as PositionCategory[]) {
    const posDecisions = preflopDecisions.filter(d => d.positionCategory === pos);
    if (posDecisions.length === 0) continue;
    
    const posVpip = posDecisions.filter(d => d.action !== 'fold').length;
    const posPfr = posDecisions.filter(d => d.action === 'raise' || d.action === 'bet').length;
    const posPostflop = postflopDecisions.filter(d => d.positionCategory === pos);
    const posBets = posPostflop.filter(d => d.action === 'bet' || d.action === 'raise').length;
    const posCalls = posPostflop.filter(d => d.action === 'call').length;
    
    positionStats.push({
      position: pos,
      vpip: posVpip / posDecisions.length * 100,
      pfr: posPfr / posDecisions.length * 100,
      aggressionFactor: posCalls > 0 ? posBets / posCalls : posBets,
      handsPlayed: posVpip,
      totalHands: posDecisions.length,
    });
  }
  
  // Aggregate strengths/weaknesses
  const topStrengths: string[] = [];
  const topWeaknesses: string[] = [];
  
  for (const metric of metrics) {
    if (metric.score >= 7.5 && metric.strengths.length > 0) {
      topStrengths.push(` ${metric.displayName}: ${metric.strengths[0]}`);

    }
    if (metric.score < 6 && metric.weaknesses.length > 0) {
      topWeaknesses.push(` ${metric.displayName}: ${metric.weaknesses[0]}`);

    }
  }
  
  // Get key mistakes and best plays
  const allEvaluations = metrics.flatMap(m => m.evaluations);
  const keyMistakes = allEvaluations
    .filter(e => (e.quality === 'poor' || e.quality === 'suboptimal') && e.detailedExample)
    .sort((a, b) => a.score - b.score)
    .slice(0, 5);
  
  const bestPlays = allEvaluations
    .filter(e => e.quality === 'excellent')
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);
  
  // Collect all detailed examples
  const detailedExamples = metrics
    .flatMap(m => m.keyExamples)
    .slice(0, 10);
  
  // Recommended drills from worst metrics
  const recommendedDrills: string[] = [];
  const worstMetrics = [...metrics].sort((a, b) => a.score - b.score).slice(0, 2);
  for (const metric of worstMetrics) {
    recommendedDrills.push(...metric.drills.slice(0, 2));
  }
  
  // 🃏 HAND-BY-HAND REVIEW: one row per hand, anchored on that hand's most
  // decisive evaluated decision (a mistake outranks a good play), so the
  // report can walk the player through the exact hands they just played.
  const cardStr = (c: any) => `${c?.rank || '?'}${(c?.suit || '?').charAt(0)}`;
  const byHand = new Map<number, DecisionEvaluation[]>();
  for (const ev of allEvaluations) {
    const hn = ev.decision?.handNumber;
    if (typeof hn !== 'number') continue;
    if (!byHand.has(hn)) byHand.set(hn, []);
    byHand.get(hn)!.push(ev);
  }
  const keyHands = [...byHand.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([hn, evs]) => {
      const worst = [...evs].sort((x, y) => x.score - y.score)[0];
      const best = [...evs].sort((x, y) => y.score - x.score)[0];
      const isMistake = worst.quality === 'poor' || worst.quality === 'suboptimal';
      const anchor = isMistake ? worst : best;
      const d = anchor.decision;
      const chipAction = d.action === 'raise' || d.action === 'all-in' || d.action === 'call' || d.action === 'bet';
      // Full action line for the hand plus its outcome - the coach talks
      // through the whole hand, not just the anchor decision.
      const handDecisions = session.decisions
        .filter(dd => dd.handNumber === hn)
        .sort((a, b) => a.timestamp - b.timestamp);
      const outcomeDecision = handDecisions.find(dd => typeof dd.handWon === 'boolean');
      return {
        handNumber: hn,
        position: String(d.position || ''),
        holeCards: (d.holeCards || []).map(cardStr),
        board: (d.communityCards || []).map(cardStr),
        potSize: d.potSizeBeforeAction || 0,
        street: d.street as any,
        userAction: chipAction && d.betAmount ? `${d.action} ${d.betAmount}` : String(d.action),
        userAmount: chipAction ? (d.betAmount || 0) : 0,
        correctAction: anchor.advice || '',
        wasCorrect: anchor.quality === 'excellent' || anchor.quality === 'good',
        isMistake,
        isGoodPlay: anchor.quality === 'excellent',
        explanation: [anchor.reason, anchor.advice].filter(Boolean).join(' '),
        conceptsTested: [...new Set(evs.map(e => e.metric))],
        // Deep decision data for the AI coach's hand-level analysis
        actionRaw: String(d.action),
        betAmount: d.betAmount || 0,
        equity: d.equity || 0,
        potOdds: d.potOdds || 0,
        requiredEquity: d.requiredEquity || 0,
        facingBet: !!d.facingBet,
        facingRaise: !!d.facingRaise,
        isFirstToAct: !!d.isFirstToAct,
        playersInHand: d.playersInHand || 0,
        effectiveStack: d.effectiveStack || 0,
        stackSize: d.stackSize || 0,
        bigBlind: (d as any).bigBlind || 0,
        handDescription: d.handDescription || '',
        actions: handDecisions.map(dd => ({
          street: String(dd.street),
          action: String(dd.action),
          amount: dd.betAmount || 0,
          pot: dd.potSizeBeforeAction || 0,
        })),
        ...(outcomeDecision ? {
          won: !!outcomeDecision.handWon,
          profit: outcomeDecision.profitLoss || 0,
          showdown: !!outcomeDecision.showdownReached,
        } : {}),
      };
    });

  // Next steps guide based on skill level
  const nextStepsGuide = getNextStepsGuide(skillLevel, topWeaknesses);
  
  // Collect relevant glossary terms
  const relevantTerms: Set<string> = new Set();
  for (const metric of metrics) {
    metric.conceptDefinitions.forEach(term => relevantTerms.add(term));
  }
  const glossaryTerms: typeof POKER_GLOSSARY = {};
  relevantTerms.forEach(term => {
    if (POKER_GLOSSARY[term]) {
      glossaryTerms[term] = POKER_GLOSSARY[term];
    }
  });
  
  const report: TrainingReport = {
    reportId: `report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    sessionId: session.sessionId,
    uid: session.uid,
    username: session.username,
    
    generatedAt: Date.now(),
    sessionDuration: session.duration || 0,
    
    overallScore,
    skillLevel,
    handsPlayed: session.handsPlayed,
    decisionsAnalyzed: session.decisions.length,
    
    stats: {
      vpip,
      pfr,
      aggressionFactor,
      cbetFrequency,
      bluffFrequency,
      wtsd,
      wonAtShowdown: wonAtShowdownPct,
    },
    
    positionStats,
    metrics,
    
    topStrengths: topStrengths.slice(0, 3),
    topWeaknesses: topWeaknesses.slice(0, 3),
    keyMistakes,
    bestPlays,
    recommendedDrills: [...new Set(recommendedDrills)].slice(0, 5),
    
    glossaryTerms,
    detailedExamples,
    nextStepsGuide,
    keyHands,
    ...(session.challenge ? { challenge: session.challenge } : {}),
    ...(session.challengeOutcome ? { challengeOutcome: session.challengeOutcome } : {}),
  };
  
  console.log(`✅ [TrainingAnalysis] Report generated: ${skillLevel} - Score ${overallScore.toFixed(1)}/10`);
  return report;
}

function getNextStepsGuide(skillLevel: string, weaknesses: string[]): string[] {
  const guides: Record<string, string[]> = {
    'Beginner': [
      ' Focus on preflop fundamentals first - learn which hands to play from each position',

      ' Memorize: Only play top 15% of hands from early position',

      ' Position is EVERYTHING - play tighter early, looser on the button',

      ' Next session: Try to fold more preflop and only play premium hands',

    ],
    'Developing': [
      ' Work on your continuation betting - bet 50-70% of pots you raised preflop',

      ' Start learning pot odds - memorize flush draw (35%), OESD (31%), gutshot (17%)',

      ' Increase aggression from late position - raise more, call less',

      ' Next session: Focus on betting strong hands for value',

    ],
    'Intermediate': [
      ' Add semi-bluffs to your game - bet your draws aggressively',

      ' Refine your bet sizing - use 50-75% pot as default',

      ' Think about opponent ranges, not just your own hand',

      ' Next session: Identify one bluff spot per orbit',

    ],
    'Advanced': [
      ' Balance your ranges - mix value bets and bluffs at correct frequencies',

      ' Study GTO concepts - minimum defense frequency, polarized ranges',

      ' Exploit weak players while staying balanced vs unknowns',

      ' Next session: Focus on thin value bets on the river',

    ],
    'Expert': [
      ' Focus on exploitative adjustments based on opponent tendencies',

      ' Review your sessions for marginal spots to optimize',

      ' Experiment with solver-approved lines in unusual spots',

      ' Next session: Document one interesting spot to analyze later',

    ],
  };
  
  return guides[skillLevel] || guides['Beginner'];
}

export default {
  analyzeTrainingSession,
  POKER_GLOSSARY,
};